import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Table,
  Pagination,
} from "react-bootstrap";
import {
  collection,
  collectionGroup,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
  updateDoc,
  doc,
  setDoc,
  writeBatch,
  runTransaction,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import swal from "sweetalert";
import { db, storage } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { normalizeBookingHn } from "./BookingEventUtils";
import {
  SCORED_SLEEP_STUDY_UPLOAD_TYPE,
  UNSCORED_SLEEP_STUDY_UPLOAD_TYPE,
  isScoredSleepStudyUpload,
  splitSleepStudyUploads,
  isSleepStudyCollection,
  getSleepStudyFileCollection,
  getSleepStudyUploadCollection,
} from "../../../utils/sleepStudyFiles";
import { loadPatientSleepStudyFiles } from "../../../utils/loadSleepStudyFiles";
import moneyBag from "../../../images/money-bag.png";
import house from "../../../images/house.png";
import { notifyError, notifyTopRight, notifyWarning } from "../toast/SetToast";
import BookingMonitorScoredSources from "./BookingMonitorScoredSources";
import {
  buildReportValidationClearProps,
  buildReportValidationClearUpdates,
  getTechnicianReportCheckErrors,
  hasTechnicianReportCheckError,
  hasTechnicianCheckDoctorCopy,
  isSameTechnicianReportError,
} from "./BookingTechnicianReportCheck";
import {
  ASSIGNED_TO_DOCTOR_STATUS,
  BOOKING_UPLOAD_BOOKING_PATH_FIELD,
  buildSleepstudyUploadMetadata,
  buildScoredCaseReviewProps,
  buildScoredUploadWaitProps,
  buildReturnedScoredCaseProps,
  compareSleepStudyMonitorCases,
  DOCTOR_SCORED_STATUS,
  getMonitorFileCounts,
  getSleepStudyDeadlineInfo,
  getSleepStudyMonitorDisplayDate,
  getMonitorSaveBlockReason,
  getScoredCaseStatus,
  getScoredCaseSourceChoices,
  getPendingScoredUploadPaths,
  hasRawSleepStudyUpload,
  isScoredCaseReviewRequired,
  isScoredCaseReadyForDoctor,
  isDirectTechnicianScoredTransition,
  isUnmarkedSleepstudyZipUpload,
  REVIEW_REQUIRED_STATUS,
  SLEEP_TEST_SCHEDULED_STATUS,
} from "./BookingMonitorWorkflow";

const TABLE_STATUS_OPTIONS = [
  SLEEP_TEST_SCHEDULED_STATUS,
  "Transferred To Center",
  "Assigned",
  "Technician Scored",
  "Assigned to Doctor",
];

const QUERY_STATUS_OPTIONS = [...TABLE_STATUS_OPTIONS, DOCTOR_SCORED_STATUS];

const WORKLOAD_STATUS_OPTIONS = [
  "Transferred To Center",
  "Assigned",
  "Technician Scored",
  "Assigned to Doctor",
];

const EDIT_STATUS_OPTIONS = [
  SLEEP_TEST_SCHEDULED_STATUS,
  "Transferred To Center",
  "Assigned",
  "Technician Scored",
  "Assigned to Doctor",
  DOCTOR_SCORED_STATUS,
];

const SORT_OPTIONS = [
  { value: "deadline", label: "Deadline Priority" },
  { value: "workflow", label: "Workflow Priority" },
  { value: "newest", label: "Newest Sleep Study" },
  { value: "oldest", label: "Oldest Sleep Study" },
];

const STATUS_VARIANT = {
  [SLEEP_TEST_SCHEDULED_STATUS]: "#005DEA",
  "Transferred To Center": "#fab2a2",
  Assigned: "#40C4FF",
  "Technician Scored": "#00CEAB",
  "Assigned to Doctor": "#9ef542",
  "Doctor Scored": "#26b99a",
};

const DOWNLOAD_VARIANT = {
  waiting_upload: "secondary",
  downloading: "warning",
  downloaded: "success",
  already_exists: "success",
  failed: "danger",
  disk_space_error: "danger",
  center_disk_space_error: "danger",
  idle: "secondary",
};

const EXTRACT_VARIANT = {
  waiting_upload: "secondary",
  waiting_download: "info",
  waiting_scored_files: "warning",
  waiting_extract: "warning",
  extracting: "warning",
  done: "success",
  [REVIEW_REQUIRED_STATUS]: "warning",
  partial_error: "warning",
  error: "danger",
  disk_space_error: "danger",
  center_disk_space_error: "danger",
  idle: "secondary",
};

const TRANSFER_VARIANT = {
  idle: "secondary",
  ready: "primary",
  copied_to_client: "warning",
  returned_to_center: "success",
  waiting_scored_files: "warning",
  [REVIEW_REQUIRED_STATUS]: "warning",
  self_scored: "info",
  error: "danger",
  disk_space_error: "danger",
  client_disk_space_error: "danger",
  center_disk_space_error: "danger",
};

const PER_PAGE = 50;

const DISK_SPACE_TRANSFER_STATUSES = [
  "disk_space_error",
  "client_disk_space_error",
  "center_disk_space_error",
];

const isDiskSpaceTransferStatus = (status) =>
  DISK_SPACE_TRANSFER_STATUSES.includes(status);

const DEADLINE_TEXT_CLASS = {
  normal: "text-muted",
  warning: "text-warning",
  danger: "text-danger",
};

const compactBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: 18,
  lineHeight: 1,
  whiteSpace: "nowrap",
  padding: "3px 6px",
};

const formatDateTime = (value) => {
  if (!value) return "-";

  let d = value;

  if (value && typeof value.toDate === "function") {
    d = value.toDate();
  } else if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    d = new Date(value.seconds * 1000);
  } else {
    d = new Date(value);
  }

  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDate = (value) => {
  if (!value) return "";

  let d = value;

  if (value && typeof value.toDate === "function") {
    d = value.toDate();
  } else if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    d = new Date(value.seconds * 1000);
  } else {
    d = new Date(value);
  }

  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateOnly = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatQuickFilterLabel = (label, count) =>
  count > 0 ? `${label} (${count})` : label;

const getAttentionQuickFilterVariant = (
  isActive,
  count,
  activeVariant,
  attentionVariant,
) =>
  isActive
    ? activeVariant
    : count > 0
      ? attentionVariant
      : "light";

const getArrayFirst = (value) =>
  Array.isArray(value) ? value[0] || "" : value || "";

const getExtractFilesList = (ep) =>
  Object.entries((ep && ep.officeExtractFiles) || {}).map(([id, file]) => ({
    sleepstudyId: id,
    ...(file || {}),
  }));

const getPrimaryExtractFile = (ep) => getExtractFilesList(ep)[0] || {};

const getPatientName = (ep) =>
  [ep && ep.firstname, ep && ep.lastname].filter(Boolean).join(" ") || "-";

const getAssignedDoctor = (ep) => (ep && ep.doctor) || "กรุณาเลือก";
const getAssignedTech = (ep) => (ep && ep.technician) || "กรุณาเลือก";
const getTechRecord = (ep) => (ep && ep.techrecord) || "กรุณาเลือก";

const getTypeSymbol = (type) => {
  if (type === "Split Night Sleep Study") return "[SN]";
  if (type === "Full Night Sleep Study") return "[FN]";
  if (type === "Home Full Night Sleep Study") return "[HN]";
  if (type === "CPAP Titration" || type === "CPAP Titration Study")
    return "[CT]";
  if (type === "Overnight Pulse Oximetry Study") return "[OPO]";
  if (type === "Multiple Sleep Latency Test") return "[MSLT]";
  return "";
};

const getDownloadStatus = (ep) => {
  const base = (ep && ep.officeDownloadStatus) || "idle";
  if (
    ep &&
    ep.status === "Transferred To Center" &&
    !hasRawSleepStudyUpload(ep) &&
    (base === "idle" || !base)
  ) {
    return "waiting_upload";
  }
  return base;
};

const getExtractStatus = (ep) => {
  const primary = getPrimaryExtractFile(ep);
  const status = (ep && ep.officeExtractStatus) || primary.status || "idle";
  if (
    ep &&
    ep.status === "Transferred To Center" &&
    !hasRawSleepStudyUpload(ep) &&
    status === "idle"
  ) {
    return "waiting_upload";
  }
  return status;
};

const hasMultiFileReviewRequired = (ep = {}) =>
  getExtractStatus(ep) === REVIEW_REQUIRED_STATUS ||
  isScoredCaseReviewRequired(ep);

const getTechTransferStatus = (ep) => {
  const status = (ep && ep.techTransferStatus) || "idle";
  if (ep && ep.status === "Assigned" && status === "idle") return "ready";
  return status;
};

const getDoctorTransferStatus = (ep) => {
  const status = (ep && ep.doctorTransferStatus) || "idle";
  if (ep && ep.status === "Assigned to Doctor" && status === "idle") {
    return "ready";
  }
  return status;
};

const mapDownloadText = (status) => {
  if (status === "waiting_upload") return "Wait Upload";
  if (status === "idle") return "Not Started";
  if (status === "downloaded") return "Downloaded";
  if (status === "already_exists") return "Already Exists";
  if (status === "failed") return "Failed";
  if (status === "downloading") return "Downloading";
  if (status === "disk_space_error") return "Disk Space Error";
  if (status === "center_disk_space_error") return "Center Disk Error";
  return status || "-";
};

const mapExtractText = (status) => {
  if (status === "waiting_upload") return "Wait Upload";
  if (status === "idle") return "Not Started";
  if (status === "done") return "Done";
  if (status === REVIEW_REQUIRED_STATUS) return "Review Multiple Files";
  if (status === "partial_error") return "Partial Error";
  if (status === "error") return "Error";
  if (status === "extracting") return "Extracting";
  if (status === "waiting_extract") return "Waiting Extract";
  if (status === "disk_space_error") return "Disk Space Error";
  if (status === "center_disk_space_error") return "Center Disk Error";
  return status || "-";
};

const mapScoredCaseText = (status) => {
  if (status === REVIEW_REQUIRED_STATUS) return "Review Scored Sources";
  if (status === "waiting_download") return "Wait Center Download";
  if (status === "waiting_scored_files") return "Wait Scored Files";
  if (status === "downloading" || status === "failed") return mapDownloadText(status);
  return mapExtractText(status);
};

const mapTransferText = (status) => {
  if (status === "idle") return "Not Started";
  if (status === "ready") return "Ready";
  if (status === "copied_to_client") return "Copied";
  if (status === "returned_to_center") return "Returned";
  if (status === "waiting_scored_files") return "Waiting Scored Files";
  if (status === REVIEW_REQUIRED_STATUS) return "Review Scored Sources";
  if (status === "self_scored") return "Self Scored";
  if (status === "error") return "Error";
  if (status === "disk_space_error") return "Disk Space Error";
  if (status === "client_disk_space_error") return "Client Disk Error";
  if (status === "center_disk_space_error") return "Center Disk Error";
  return status || "-";
};

const getReportValidationErrors = (ep = {}) => {
  const errors = Array.isArray(ep.reportValidationErrors)
    ? [...ep.reportValidationErrors]
    : [];
  getTechnicianReportCheckErrors(ep).forEach((error) => {
    if (!errors.some((existing) => existing && existing.code === error.code && existing.actual === error.actual)) {
      errors.push(error);
    }
  });

  if (errors.length > 0) {
    return errors.filter((item) => (item && item.level) !== "warning");
  }

  if (ep.reportValidationStatus === "error" && ep.reportValidationSummary) {
    return [
      {
        level: "error",
        code: "REPORT_VALIDATION_ERROR",
        message: ep.reportValidationSummary,
      },
    ];
  }

  return [];
};

const hasReportValidationError = (ep = {}) =>
  getReportValidationErrors(ep).length > 0;

const isReportValidationDiskSpaceError = (error = {}) => {
  const text = [
    error.code,
    error.message,
    error.expected,
    error.actual,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    text.includes("disk_space") ||
    text.includes("disk space") ||
    text.includes("not enough space") ||
    text.includes("free space below")
  );
};

const getReportValidationIssueType = (errors = []) => {
  if (errors.some((error) => isReportValidationDiskSpaceError(error))) {
    return "Disk Space";
  }
  return "Report";
};

const hasReportValidationDiskSpaceError = (ep = {}) =>
  getReportValidationErrors(ep).some((error) =>
    isReportValidationDiskSpaceError(error),
  );

const hasDiskSpaceIssue = (ep = {}) =>
  isDiskSpaceTransferStatus(getDownloadStatus(ep)) ||
  isDiskSpaceTransferStatus(getExtractStatus(ep)) ||
  isDiskSpaceTransferStatus(getTechTransferStatus(ep)) ||
  isDiskSpaceTransferStatus(getDoctorTransferStatus(ep)) ||
  hasReportValidationDiskSpaceError(ep);

const hasDoctorScoredReportDiskSpaceError = (ep = {}) =>
  ep.status === DOCTOR_SCORED_STATUS && hasReportValidationDiskSpaceError(ep);

const formatReportValidationError = (error = {}) => {
  const bits = [];
  if (error.code) bits.push(error.code);
  if (error.message) bits.push(error.message);
  if (error.expected || error.actual) {
    bits.push(`Expected: ${error.expected || "-"} | Actual: ${error.actual || "-"}`);
  }
  return bits.join(" - ") || "Report validation error";
};

const getManualStatusLockLog = ({
  ep = {},
  reason = "",
  nextStatus = "",
  nextTechnician = "",
  nextDoctor = "",
}) => [
  { label: "Reason", value: reason || "-" },
  { label: "Current Status", value: ep.status || "-" },
  { label: "Next Status", value: nextStatus || "-" },
  { label: "Current Tech", value: getAssignedTech(ep) },
  { label: "Next Tech", value: nextTechnician || "-" },
  { label: "Current Doctor", value: getAssignedDoctor(ep) },
  { label: "Next Doctor", value: nextDoctor || "-" },
  { label: "Download", value: mapDownloadText(getDownloadStatus(ep)) },
  { label: "Extract", value: mapExtractText(getExtractStatus(ep)) },
  {
    label: "Scored Files",
    value: mapScoredCaseText(getScoredCaseStatus(ep)),
  },
  { label: "Tech Transfer", value: mapTransferText(getTechTransferStatus(ep)) },
  {
    label: "Doctor Transfer",
    value: mapTransferText(getDoctorTransferStatus(ep)),
  },
];

const countAssignments = (items, key) => {
  const map = {};
  items.forEach((item) => {
    const ep = (item && item.extendedProps) || {};
    const name = key === "doctor" ? getAssignedDoctor(ep) : getAssignedTech(ep);
    map[name] = (map[name] || 0) + 1;
  });

  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => {
      if (a.name === "กรุณาเลือก" && b.name !== "กรุณาเลือก") return -1;
      if (b.name === "กรุณาเลือก" && a.name !== "กรุณาเลือก") return 1;
      return b.count - a.count || a.name.localeCompare(b.name);
    });
};

const searchUsers = (options, queryText) => {
  const q = (queryText || "").trim().toLowerCase();
  if (!q) return options;

  return options.filter((row) => {
    const name = (row.name || "").toLowerCase();
    const nickname = (row.nickname || "").toLowerCase();
    return name.includes(q) || nickname.includes(q);
  });
};

const leastLoadedName = (rows) => {
  const filtered = rows
    .filter((r) => r && r.name !== "กรุณาเลือก")
    .sort((a, b) => a.count - b.count || a.name.localeCompare(b.name));

  return filtered.length === 0 ? "กรุณาเลือก" : filtered[0].name;
};

const getTimestampMs = (value) => {
  if (!value) return 0;
  if (value && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    return value.seconds * 1000;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortLatestFirst = (rows) =>
  [...rows].sort((a, b) => getTimestampMs(b.createAt) - getTimestampMs(a.createAt));

const getLatestWithFile = (rows) =>
  sortLatestFirst(rows).find(
    (row) => Array.isArray(row.reportFile) && row.reportFile.length > 0,
  ) || null;

const SummaryCard = ({
  title,
  value,
  subtitle,
  accent = "#0d6efd",
}) => (
  <Card
    className="sleep-study-summary-card shadow-sm h-100"
    style={{ borderColor: accent }}
  >
    <Card.Body className="d-flex align-items-center justify-content-between">
      <div className="sleep-study-summary-copy">
        <div className="sleep-study-summary-title">{title}</div>
        <div className="sleep-study-summary-subtitle">{subtitle}</div>
      </div>
      <div className="sleep-study-summary-value">{value}</div>
    </Card.Body>
  </Card>
);

const StatusCard = ({ label, text, tone = "secondary" }) => {
  return (
    <div className="booking-status-card">
      <div className="booking-status-label">{label}</div>
      <div className={`booking-status-pill booking-status-pill-${tone}`}>
        {text || "-"}
      </div>
    </div>
  );
};

const StopBangBadge = ({ score }) => {
  const value = Number(score);

  if (!Number.isFinite(value)) {
    return <span className="stopbang-badge stopbang-muted">STOP-BANG: -</span>;
  }

  let cls = "stopbang-green";
  let label = "Low Risk";

  if (value >= 5) {
    cls = "stopbang-red";
    label = "High Risk";
  } else if (value >= 3) {
    cls = "stopbang-orange";
    label = "Intermediate Risk";
  }

  return (
    <span className={`stopbang-badge ${cls}`}>
      STOP-BANG: {value} · {label}
    </span>
  );
};

const WorkloadTable = ({ title, rows }) => (
  <Card className="shadow-sm h-100">
    <Card.Body>
      <div className="fw-bold mb-3">{title}</div>
      <div className="table-responsive">
        <Table size="sm" bordered hover className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th style={{ width: 100 }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-center text-muted">
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${title}-${row.name}`}>
                  <td>{row.name}</td>
                  <td>
                    <span
                      className={`badge badge-${
                        row.count >= 7
                          ? "danger"
                          : row.count >= 4
                            ? "warning"
                            : "primary"
                      }`}
                    >
                      {row.count}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </Card.Body>
  </Card>
);

function SleepStudyMonitor() {
  const { currentUser } = useAuth();
  const reportInputRef = useRef(null);
  const sleepStudyInputRef = useRef(null);
  const uploadSelectionRef = useRef({ item: null, type: "report" });
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nodeInfo, setNodeInfo] = useState([]);

  const [technicianOptions, setTechnicianOptions] = useState([
    { name: "กรุณาเลือก", nickname: "กรุณาเลือก" },
  ]);

  const [doctorOptions, setDoctorOptions] = useState([
    { name: "กรุณาเลือก", nickname: "กรุณาเลือก" },
  ]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [emailFilter, setEmailFilter] = useState("All");
  const [nodeFilter, setNodeFilter] = useState("All");
  const [quickFilter, setQuickFilter] = useState("all");
  const [sortBy, setSortBy] = useState("deadline");
  const [showModal, setShowModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [editTechnician, setEditTechnician] = useState("กรุณาเลือก");
  const [editDoctor, setEditDoctor] = useState("กรุณาเลือก");
  const [editStatus, setEditStatus] = useState("Transferred To Center");
  const [reviewedScoredSourcePath, setReviewedScoredSourcePath] = useState("");
  const [saving, setSaving] = useState(false);
  const [techQuery, setTechQuery] = useState("");
  const [doctorQuery, setDoctorQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editStartDate, setEditStartDate] = useState("");
  const [editLatestSleepStudyDate, setEditLatestSleepStudyDate] = useState("");
  const [isEditingLatestSleepStudyDate, setIsEditingLatestSleepStudyDate] =
    useState(false);
  const [editNote, setEditNote] = useState("");
  const [uploadingType, setUploadingType] = useState("");
  const [fileModal, setFileModal] = useState({
    show: false,
    title: "",
    type: "report",
    files: [],
  });
  const [filePreviewModal, setFilePreviewModal] = useState({
    show: false,
    title: "",
    url: "",
  });
  const [emailModal, setEmailModal] = useState({
    show: false,
    item: null,
    emailList: [],
  });
  const [manualOverrideModal, setManualOverrideModal] = useState({
    show: false,
    reason: "",
    log: [],
  });
  const [selectedEmail, setSelectedEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [clearingReportErrorId, setClearingReportErrorId] = useState("");
  const [removingDiskSpaceErrorId, setRemovingDiskSpaceErrorId] = useState("");

  const teamOrder = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

  useEffect(() => {
    loadCases();
    loadAssigneeOptions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, emailFilter, nodeFilter, quickFilter, sortBy]);

  const getInitialDateRange = () => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 3, 1, 0, 0, 0, 0),
      end: new Date(now.getFullYear(), now.getMonth() + 10, 0, 23, 59, 59, 999),
    };
  };

  const loadAssigneeOptions = async () => {
    try {
      const [techDocs, docDocs] = await Promise.all([
        getDocs(collection(db, "sleeptechnicians")),
        getDocs(collection(db, "doctors")),
      ]);

      const allTechs = [
        { name: "กรุณาเลือก", nickname: "กรุณาเลือก" },
        ...techDocs.docs
          .filter((doc) => doc.data().status === "Available")
          .sort((a, b) => {
            const teamA = a.data().team || "";
            const teamB = b.data().team || "";
            const indexA = teamOrder.indexOf(teamA);
            const indexB = teamOrder.indexOf(teamB);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return teamA.localeCompare(teamB, "th");
          })
          .map((doc) => ({
            name: doc.data().name || "",
            nickname: doc.data().nickname || doc.data().name || "",
          })),
      ];

      const allDocs = [
        { name: "กรุณาเลือก", nickname: "กรุณาเลือก" },
        ...docDocs.docs.map((doc) => {
          const data = doc.data();
          const fullName = data.name || "";
          const nickname = data.fullnameTh
            ? data.fullnameTh.split(/\s+/).slice(0, 2).join(" ")
            : fullName;

          return {
            id: doc.id,
            name: fullName,
            nickname,
            email: data.email || "",
          };
        }),
      ];

      setTechnicianOptions(allTechs);
      setDoctorOptions(allDocs);
    } catch (error) {
      console.error("loadAssigneeOptions error", error);
    }
  };

  const loadCases = async () => {
    setLoading(true);

    try {
      const { start, end } = getInitialDateRange();
      const rangeStartTs = Timestamp.fromDate(start);
      const rangeEndTs = Timestamp.fromDate(end);

      const nodesPromise = getDocs(collection(db, "nodes"));
      const q = query(
        collectionGroup(db, "bookings"),
        where("extendedProps.status", "in", QUERY_STATUS_OPTIONS),
        where("start", ">=", rangeStartTs),
        where("start", "<=", rangeEndTs),
        orderBy("start", "desc"),
      );

      const [snap, nodesSnap] = await Promise.all([getDocs(q), nodesPromise]);
      const loadedNodeInfo = nodesSnap.docs.map((nodeDoc) => nodeDoc.data());
      setNodeInfo(loadedNodeInfo);

      const bookingRows = snap.docs
        .map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            ...data,
            id: docSnap.ref.path,
            docId: docSnap.id,
            ref: docSnap.ref,
            extendedProps: data.extendedProps || {},
          };
        })
        .filter((booking) => normalizeBookingHn(booking.extendedProps.hn) !== "");

      const rows = await Promise.all(
        bookingRows.map(async (booking) => {
          try {
            const ep = booking.extendedProps || {};
            const hn = ep.hn || "";

            if (!hn) {
              return {
                ...booking,
                extendedProps: {
                  ...ep,
                  reports: [],
                  sleepstudy: [],
                  latestSleepstudyWithFile: null,
                },
              };
            }

            const [reportSnap, sleepstudyDocs] = await Promise.all([
              getDocs(collection(db, "patients", hn, "report")),
              loadPatientSleepStudyFiles(hn),
            ]);

            const reportDocs = sortLatestFirst(
              reportSnap.docs.map((reportDoc) => ({
                id: reportDoc.id,
                ...reportDoc.data(),
              })),
            );

            const sortedSleepstudyDocs = sortLatestFirst(sleepstudyDocs);
            const latestSleepstudyWithFile =
              getLatestWithFile(sortedSleepstudyDocs);

            return {
              ...booking,
              extendedProps: {
                ...ep,
                reports: reportDocs,
                sleepstudy: sortedSleepstudyDocs,
                latestSleepstudyWithFile,
              },
            };
          } catch (err) {
            console.error("sleepstudy fetch error", booking.id, err);
            return {
              ...booking,
              extendedProps: {
                ...(booking.extendedProps || {}),
                reports: [],
                sleepstudy: [],
                latestSleepstudyWithFile: null,
              },
            };
          }
        }),
      );

      setCases(rows);
    } catch (error) {
      console.error("loadCases error", error);
    } finally {
      setLoading(false);
    }
  };

  const nodeFilterOptions = useMemo(() => {
    if (nodeInfo.length > 0) {
      return [...nodeInfo]
        .filter((node) => node && node.nodename)
        .sort((a, b) => {
          const aIndex = Number(a.nodeIndex || 0);
          const bIndex = Number(b.nodeIndex || 0);
          if (aIndex !== bIndex) return aIndex - bIndex;
          return String(a.nodename).localeCompare(String(b.nodename), "th");
        })
        .map((node) => node.nodename);
    }

    const nodes = new Set();
    cases.forEach((item) => {
      const nodeName = (item.extendedProps && item.extendedProps.node) || "";
      if (nodeName) nodes.add(nodeName);
    });
    return Array.from(nodes).sort((a, b) => a.localeCompare(b, "th"));
  }, [cases, nodeInfo]);

  const casesMatchingFilters = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return cases.filter((item) => {
      const ep = item.extendedProps || {};
      if (!QUERY_STATUS_OPTIONS.includes(ep.status)) return false;

      const text = [
        getPatientName(ep),
        ep.hn,
        ep.order,
        ep.node,
        ep.healthInsurance,
        ep.note,
        ep.type,
        getTechRecord(ep),
        getAssignedTech(ep),
        getAssignedDoctor(ep),
        ep.status,
        ep.techTransferStatus,
        ep.doctorTransferStatus,
        ep.officeDownloadStatus,
        ep.officeExtractStatus,
        ep.officeScoreExtractStatus,
        ep.officeDownloadError,
        ep.officeExtractError,
        ep.officeScoreExtractError,
        ep.techTransferError,
        ep.doctorTransferError,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchSearch = keyword ? text.includes(keyword) : true;
      const matchStatus =
        statusFilter === "All" ? true : ep.status === statusFilter;
      const hasSentEmail = !!ep.email;
      const matchEmail =
        emailFilter === "All"
          ? true
          : emailFilter === "Sent"
            ? hasSentEmail
            : !hasSentEmail;
      const hasReportError = hasReportValidationError(ep);
      const hasDiskSpaceError = hasDiskSpaceIssue(ep);

      if (
        ep.status === DOCTOR_SCORED_STATUS &&
        statusFilter !== DOCTOR_SCORED_STATUS &&
        !keyword &&
        !hasReportError &&
        !hasDiskSpaceError
      ) {
        return false;
      }
      if (
        ep.status === DOCTOR_SCORED_STATUS &&
        statusFilter !== DOCTOR_SCORED_STATUS &&
        !matchSearch &&
        !hasReportError &&
        !hasDiskSpaceError
      ) {
        return false;
      }

      const needTech =
        ep.status === "Assigned" && getAssignedTech(ep) === "กรุณาเลือก";

      const needDoctor =
        ep.status === "Assigned to Doctor" &&
        getAssignedDoctor(ep) === "กรุณาเลือก";

      const needsMultiFileReview = hasMultiFileReviewRequired(ep);
      const scoredStatus = getScoredCaseStatus(ep, item.ref ? item.ref.path : "");

      const hasIssue =
        getDownloadStatus(ep) === "failed" ||
        getExtractStatus(ep) === "error" ||
        needsMultiFileReview ||
        ["failed", "error", "partial_error"].includes(scoredStatus) ||
        getTechTransferStatus(ep) === "error" ||
        getDoctorTransferStatus(ep) === "error" ||
        hasDiskSpaceError ||
        hasReportError;

      const isWaitingUpload = getDownloadStatus(ep) === "waiting_upload" ||
        scoredStatus === "waiting_upload";

      const waitingTechCopy =
        ep.status === "Assigned" && getTechTransferStatus(ep) === "ready";

      const waitingTechReturn =
        ep.status === "Technician Scored" &&
        ["copied_to_client", "ready"].includes(getTechTransferStatus(ep));

      const waitingDoctorCopy =
        ep.status === "Assigned to Doctor" &&
        getDoctorTransferStatus(ep) === "ready";

      const matchQuick =
        quickFilter === "all"
          ? true
          : quickFilter === "need-tech"
            ? needTech
            : quickFilter === "need-doctor"
              ? needDoctor
              : quickFilter === "issues"
                ? hasIssue
                : quickFilter === "needs-review"
                  ? needsMultiFileReview
                : quickFilter === "report-errors"
                  ? hasReportError
                  : quickFilter === "waiting-upload"
                    ? isWaitingUpload
                    : quickFilter === "waiting-tech-copy"
                      ? waitingTechCopy
                      : quickFilter === "waiting-tech-return"
                        ? waitingTechReturn
                        : quickFilter === "waiting-doctor-copy"
                          ? waitingDoctorCopy
                          : true;

      return matchSearch && matchStatus && matchEmail && matchQuick;
    });
  }, [cases, search, statusFilter, emailFilter, quickFilter]);

  const nodeCaseCounts = useMemo(() => {
    const nodeCodes = new Map(
      nodeInfo.filter(Boolean).map((node) => [node.nodename, String(node.nodecode || "").trim()]),
    );
    const totals = new Map();
    casesMatchingFilters.forEach((item) => {
      const nodeName = (item.extendedProps || {}).node;
      if (nodeName) totals.set(nodeName, (totals.get(nodeName) || 0) + 1);
    });
    const nodeNames = Array.from(new Set([
      ...nodeFilterOptions,
      ...cases.map((item) => (item.extendedProps || {}).node)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "th")),
    ]));
    return nodeNames
      .map((nodeName) => ({
        nodeName,
        nodeCode: nodeCodes.get(nodeName) || nodeName,
        count: totals.get(nodeName) || 0,
      }))
      .filter((node) => node.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [cases, casesMatchingFilters, nodeFilterOptions, nodeInfo]);

  const filteredCases = useMemo(() => {
    return casesMatchingFilters
      .filter((item) => nodeFilter === "All" || (item.extendedProps || {}).node === nodeFilter)
      .sort((a, b) => compareSleepStudyMonitorCases(a, b, { sortBy, nodeInfo }));
  }, [casesMatchingFilters, nodeFilter, sortBy, nodeInfo]);

  const counts = useMemo(() => {
    const base = {
      transferred: { total: 0, downloaded: 0, extracted: 0, waitingUpload: 0 },
      assigned: 0,
      technicianScored: 0,
      assignedToDoctor: 0,
      downloadErrors: 0,
      extractErrors: 0,
      transferErrors: 0,
      reportErrors: 0,
      reviewRequired: 0,
      waitingUpload: 0,
      needTech: 0,
      needDoctor: 0,
      techReady: 0,
      waitingTechReturn: 0,
      techCopied: 0,
      techReturned: 0,
      techSelfScored: 0,
      doctorReady: 0,
      doctorCopied: 0,
      doctorReturned: 0,
    };

    cases.forEach((item) => {
      const ep = item.extendedProps || {};
      const hasReportError = hasReportValidationError(ep);
      if (!WORKLOAD_STATUS_OPTIONS.includes(ep.status)) {
        if (hasDoctorScoredReportDiskSpaceError(ep)) {
          base.reportErrors += 1;
        }
        return;
      }

      const downloadStatus = getDownloadStatus(ep);
      const extractStatus = getExtractStatus(ep);
      const techTransfer = getTechTransferStatus(ep);
      const doctorTransfer = getDoctorTransferStatus(ep);
      const scoredStatus = getScoredCaseStatus(ep, item.ref ? item.ref.path : "");

      if (hasMultiFileReviewRequired(ep)) {
        base.reviewRequired += 1;
      }

      if (ep.status === "Transferred To Center") {
        base.transferred.total += 1;
        if (downloadStatus === "downloaded") base.transferred.downloaded += 1;
        if (extractStatus === "done") base.transferred.extracted += 1;
        if (downloadStatus === "waiting_upload") {
          base.transferred.waitingUpload += 1;
        }
      }

      if (ep.status === "Assigned") base.assigned += 1;
      if (ep.status === "Technician Scored") base.technicianScored += 1;
      if (ep.status === "Assigned to Doctor") base.assignedToDoctor += 1;

      if (
        downloadStatus === "failed" ||
        downloadStatus === "disk_space_error" ||
        scoredStatus === "failed"
      ) {
        base.downloadErrors += 1;
      }
      if (extractStatus === "error" || extractStatus === "disk_space_error" ||
          ["error", "partial_error"].includes(scoredStatus)) {
        base.extractErrors += 1;
      }
      if (
        techTransfer === "error" ||
        isDiskSpaceTransferStatus(techTransfer) ||
        doctorTransfer === "error" ||
        isDiskSpaceTransferStatus(doctorTransfer)
      ) {
        base.transferErrors += 1;
      }
      if (hasReportError) {
        base.reportErrors += 1;
      }
      if (downloadStatus === "waiting_upload" || scoredStatus === "waiting_upload") {
        base.waitingUpload += 1;
      }

      if (ep.status === "Assigned" && getAssignedTech(ep) === "กรุณาเลือก") {
        base.needTech += 1;
      }

      if (
        ep.status === "Assigned to Doctor" &&
        getAssignedDoctor(ep) === "กรุณาเลือก"
      ) {
        base.needDoctor += 1;
      }

      if (techTransfer === "ready") base.techReady += 1;
      if (
        ep.status === "Technician Scored" &&
        ["copied_to_client", "ready"].includes(techTransfer)
      ) {
        base.waitingTechReturn += 1;
      }
      if (techTransfer === "copied_to_client") base.techCopied += 1;
      if (techTransfer === "returned_to_center") base.techReturned += 1;
      if (techTransfer === "self_scored") base.techSelfScored += 1;

      if (doctorTransfer === "ready") base.doctorReady += 1;
      if (doctorTransfer === "copied_to_client") base.doctorCopied += 1;
      if (doctorTransfer === "returned_to_center") base.doctorReturned += 1;
    });

    return base;
  }, [cases]);

  const issueFilterCount =
    counts.downloadErrors +
    counts.extractErrors +
    counts.transferErrors +
    counts.reportErrors +
    counts.reviewRequired;

  const allTechWorkload = useMemo(
    () =>
      countAssignments(
        cases.filter((c) =>
          WORKLOAD_STATUS_OPTIONS.includes((c.extendedProps || {}).status),
        ),
        "technician",
      ),
    [cases],
  );

  const allDoctorWorkload = useMemo(
    () =>
      countAssignments(
        cases.filter((c) =>
          WORKLOAD_STATUS_OPTIONS.includes((c.extendedProps || {}).status),
        ),
        "doctor",
      ),
    [cases],
  );

  const issueCases = useMemo(
    () => {
      const source = [...filteredCases];
      const seen = new Set(source.map((item) => item.id));

      cases.forEach((item) => {
        const ep = item.extendedProps || {};
        if (!hasDoctorScoredReportDiskSpaceError(ep) || seen.has(item.id)) {
          return;
        }
        seen.add(item.id);
        source.push(item);
      });

      return source
        .map((item) => {
          const ep = item.extendedProps || {};
          const primary = getPrimaryExtractFile(ep);
          const downloadStatus = getDownloadStatus(ep);
          const extractStatus = getExtractStatus(ep);
          const scoreExtractStatus = ep.officeScoreExtractStatus || "idle";
          const scoredStatus = getScoredCaseStatus(ep, item.ref ? item.ref.path : "");
          const techTransfer = getTechTransferStatus(ep);
          const doctorTransfer = getDoctorTransferStatus(ep);
          const reportValidationErrors = getReportValidationErrors(ep);
          const latestSleepstudyWithFile = ep.latestSleepstudyWithFile || null;
          const workflow = `${ep.status || "-"}`;

          const zipName =
            getArrayFirst(
              latestSleepstudyWithFile && latestSleepstudyWithFile.reportFile,
            ) ||
            getArrayFirst(ep.officeDownloadedFileName) ||
            getArrayFirst(ep.officeScoreDownloadedFileName) ||
            primary.zipFileName ||
            "-";

          const localZipName =
            getArrayFirst(ep.officeDownloadedLocalFileName) ||
            getArrayFirst(ep.officeScoreDownloadedLocalFileName) ||
            primary.localZipFileName ||
            "-";

          let errorType = "";
          let errorText = "";

          if (
            extractStatus === REVIEW_REQUIRED_STATUS ||
            isScoredCaseReviewRequired(ep)
          ) {
            errorType = "Review Required";
            errorText =
              (scoreExtractStatus === REVIEW_REQUIRED_STATUS
                ? ep.officeScoreExtractError
                : ep.officeExtractError) ||
              "Multiple sleep study files are preserved separately. Admin must review/merge them before changing status.";
          } else if (reportValidationErrors.length > 0) {
            errorType = getReportValidationIssueType(reportValidationErrors);
            errorText = formatReportValidationError(reportValidationErrors[0]);
          } else if (scoredStatus === "waiting_upload") {
            errorType = "Waiting Scored Upload";
            errorText = "Waiting for the outside technician's scored ZIP. Doctor assignment is blocked.";
          } else if (scoredStatus === "failed") {
            errorType = "Scored Download";
            errorText = ep.officeScoreDownloadError || "Center could not download the scored ZIP.";
          } else if (["error", "partial_error"].includes(scoredStatus)) {
            errorType = "Scored Extract";
            errorText = ep.officeScoreExtractError || "Wait until every scored ZIP is extracted successfully.";
          } else if (downloadStatus === "waiting_upload") {
            errorType = "Waiting Upload";
            errorText = "Technician has not uploaded ZIP/reportFile yet";
          } else if (downloadStatus === "failed") {
            errorType = "Download";
            errorText = ep.officeDownloadError || "";
          } else if (isDiskSpaceTransferStatus(downloadStatus)) {
            errorType = "Disk Space";
            errorText = ep.officeDownloadError || "";
          } else if (extractStatus === "error") {
            errorType = "Extract";
            errorText = primary.error || ep.officeExtractError || "";
          } else if (isDiskSpaceTransferStatus(extractStatus)) {
            errorType = "Disk Space";
            errorText = primary.error || ep.officeExtractError || "";
          } else if (techTransfer === "error") {
            errorType = "Tech Transfer";
            errorText = ep.techTransferError || "";
          } else if (techTransfer === "client_disk_space_error") {
            errorType = "Client Disk Space";
            errorText = ep.techTransferError || "";
          } else if (techTransfer === "center_disk_space_error") {
            errorType = "Center Disk Space";
            errorText = ep.techTransferError || "";
          } else if (techTransfer === "disk_space_error") {
            errorType = "Disk Space";
            errorText = ep.techTransferError || "";
          } else if (doctorTransfer === "error") {
            errorType = "Doctor Transfer";
            errorText = ep.doctorTransferError || "";
          } else if (doctorTransfer === "client_disk_space_error") {
            errorType = "Client Disk Space";
            errorText = ep.doctorTransferError || "";
          } else if (doctorTransfer === "center_disk_space_error") {
            errorType = "Center Disk Space";
            errorText = ep.doctorTransferError || "";
          } else if (doctorTransfer === "disk_space_error") {
            errorType = "Disk Space";
            errorText = ep.doctorTransferError || "";
          }

          if (!errorType) return null;

          return {
            id: item.id,
            item,
            patientName: getPatientName(ep),
            techrecord: getTechRecord(ep),
            doctorScore: getAssignedDoctor(ep),
            order: ep.order || item.title || "-",
            zipName,
            localZipName,
            errorType,
            errorText,
            canRemoveDiskSpaceError: [
              "Disk Space",
              "Client Disk Space",
              "Center Disk Space",
            ].includes(errorType),
            canClearReportError: reportValidationErrors.length > 0,
            workflow,
          };
        })
        .filter(Boolean);
    },
    [cases, filteredCases],
  );

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PER_PAGE));

  const paginatedCases = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * PER_PAGE;
    return filteredCases.slice(startIndex, startIndex + PER_PAGE);
  }, [filteredCases, currentPage, totalPages]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginationItems = useMemo(() => {
    const items = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let number = startPage; number <= endPage; number += 1) {
      const active = number === currentPage;

      items.push(
        <Pagination.Item
          key={number}
          active={active}
          onClick={() => setCurrentPage(number)}
          style={{
            minWidth: 44,
            textAlign: "center",
            userSelect: "none",
            color: active ? "#fff" : "#6c757d",
            backgroundColor: active ? "#7b1fa2" : "#fff",
            borderColor: "#dee2e6",
            fontWeight: 500,
          }}
        >
          {number}
        </Pagination.Item>,
      );
    }

    return items;
  }, [currentPage, totalPages]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCase(null);
    setTechQuery("");
    setDoctorQuery("");
    setEditStartDate("");
    setEditLatestSleepStudyDate("");
    setIsEditingLatestSleepStudyDate(false);
    setEditNote("");
    setManualOverrideModal({ show: false, reason: "", log: [] });
  };

  const openEditModal = (item) => {
    const ep = item.extendedProps || {};
    setSelectedCase(item);
    setReviewedScoredSourcePath("");
    setEditTechnician(getAssignedTech(ep));
    setEditDoctor(getAssignedDoctor(ep));
    setEditStatus(ep.status || "Transferred To Center");
    setEditStartDate(toInputDate(item.start));
    setEditLatestSleepStudyDate(
      toInputDate(ep.latestSleepStudyDate || item.start),
    );
    setIsEditingLatestSleepStudyDate(false);
    setEditNote(ep.note || "");
    setTechQuery("");
    setDoctorQuery("");
    setShowModal(true);
  };

  const techPickerRows = useMemo(() => {
    return searchUsers(technicianOptions, techQuery)
      .map((r) => {
        const found = allTechWorkload.find((x) => x.name === r.name);
        return {
          name: r.name,
          nickname: r.nickname || r.name,
          count: found ? found.count : 0,
        };
      })
      .sort((a, b) => {
        if (a.name === "กรุณาเลือก" && b.name !== "กรุณาเลือก") return -1;
        if (b.name === "กรุณาเลือก" && a.name !== "กรุณาเลือก") return 1;
        return a.count - b.count || a.name.localeCompare(b.name);
      });
  }, [techQuery, allTechWorkload, technicianOptions]);

  const doctorPickerRows = useMemo(() => {
    return searchUsers(doctorOptions, doctorQuery)
      .map((r) => {
        const found = allDoctorWorkload.find((x) => x.name === r.name);
        return {
          name: r.name,
          nickname: r.nickname || r.name,
          count: found ? found.count : 0,
        };
      })
      .sort((a, b) => {
        if (a.name === "กรุณาเลือก" && b.name !== "กรุณาเลือก") return -1;
        if (b.name === "กรุณาเลือก" && a.name !== "กรุณาเลือก") return 1;
        return a.count - b.count || a.name.localeCompare(b.name);
      });
  }, [doctorQuery, allDoctorWorkload, doctorOptions]);

  const leastLoadedTech = leastLoadedName(allTechWorkload);
  const leastLoadedDoctor = leastLoadedName(allDoctorWorkload);

  const doctorEditEnabled =
    editStatus === ASSIGNED_TO_DOCTOR_STATUS ||
    editStatus === DOCTOR_SCORED_STATUS;

  const getTechCount = (name) => {
    const found = allTechWorkload.find((x) => x.name === name);
    return found ? found.count : 0;
  };

  const getDoctorCount = (name) => {
    const found = allDoctorWorkload.find((x) => x.name === name);
    return found ? found.count : 0;
  };

  const updateCaseUploadRows = (targetItem, type, rows, workflowProps = {}) => {
    const propKey = isSleepStudyCollection(type) ? "sleepstudy" : "reports";
    const latestSleepstudyWithFile =
      isSleepStudyCollection(type) ? getLatestWithFile(rows) : null;

    const updateItem = (item) => {
      const sameRow =
        item.id === targetItem.id ||
        (item.ref && targetItem.ref && item.ref.path === targetItem.ref.path);

      if (!sameRow) return item;

      return {
        ...item,
        extendedProps: {
          ...(item.extendedProps || {}),
          ...workflowProps,
          [propKey]: rows.length ? rows : null,
          ...(isSleepStudyCollection(type)
            ? { latestSleepstudyWithFile }
            : {}),
        },
      };
    };

    setCases((prev) => prev.map(updateItem));
    setSelectedCase((prev) => (prev ? updateItem(prev) : prev));
  };

  const handleUploadClick = (item, type, uploadType = UNSCORED_SLEEP_STUDY_UPLOAD_TYPE) => {
    uploadSelectionRef.current = { item, type, uploadType };

    if (type === "sleepstudy") {
      sleepStudyInputRef.current && sleepStudyInputRef.current.click();
      return;
    }

    reportInputRef.current && reportInputRef.current.click();
  };

  const handleUploadChange = async (e, type) => {
    const files = Array.from(e.target.files || []);
    const targetItem = uploadSelectionRef.current.item;
    const ep = (targetItem && targetItem.extendedProps) || {};
    const hn = ep.hn || "";

    try {
      if (!targetItem) throw new Error("No booking selected");
      if (!hn) throw new Error("Missing HN");
      if (files.length === 0) return;

      setUploadingType(type);

      const report = {
        hn,
        ...buildSleepstudyUploadMetadata(
          type === "sleepstudy" ? ep : {},
          currentUser,
          type === "sleepstudy" && targetItem.ref
            ? targetItem.ref.path
            : "",
          "booking_monitor",
          type === "sleepstudy" ? uploadSelectionRef.current.uploadType : "",
        ),
      };

      const fileCollection = type === "sleepstudy"
        ? getSleepStudyUploadCollection(report.scoreUploadType)
        : type;
      const docRef = doc(collection(db, "patients", hn, fileCollection));
      const workflowProps = report.scoreUploadType === SCORED_SLEEP_STUDY_UPLOAD_TYPE
        ? buildScoredUploadWaitProps(ep) : {};
      if (Object.keys(workflowProps).length && targetItem.ref) {
        await updateDoc(targetItem.ref, Object.fromEntries(
          Object.entries(workflowProps).map(([key, value]) => [`extendedProps.${key}`, value]),
        ));
      }
      await setDoc(docRef, report);

      const uploadedNames = [];
      for (const file of files) {
        const storageRef = ref(
          storage,
          `${fileCollection}s/${hn}/${docRef.id}/${file.name}`,
        );
        await uploadBytes(storageRef, file);
        await getDownloadURL(storageRef);
        uploadedNames.push(file.name);
      }

      await updateDoc(doc(db, "patients", hn, fileCollection, docRef.id), {
        reportFile: uploadedNames,
      });

      const rows = sortLatestFirst(isSleepStudyCollection(fileCollection)
        ? await loadPatientSleepStudyFiles(hn)
        : (await getDocs(collection(db, "patients", hn, fileCollection))).docs.map((rowDoc) => ({ id: rowDoc.id, ...rowDoc.data() })));

      updateCaseUploadRows(targetItem, fileCollection, rows, workflowProps);
      notifyTopRight(`Upload ${type} success`);
    } catch (error) {
      console.error("handleUploadChange error", error);
      notifyError(error.message || `Upload ${type} failed`);
    } finally {
      setUploadingType("");
      if (reportInputRef.current) reportInputRef.current.value = "";
      if (sleepStudyInputRef.current) sleepStudyInputRef.current.value = "";
    }
  };

  const handleOpenUploadedFile = async (row, type, fileName) => {
    try {
      const hn = row && row.hn;
      const id = row && row.id;
      if (!hn || !id || !fileName) throw new Error("Missing file data");

      const fileCollection = isSleepStudyCollection(type) ? getSleepStudyFileCollection(row) : type;
      const storageRef = ref(storage, `${fileCollection}s/${hn}/${id}/${fileName}`);
      const url = await getDownloadURL(storageRef);
      setFilePreviewModal({
        show: true,
        title: fileName,
        url,
      });
    } catch (error) {
      console.error("handleOpenUploadedFile error", error);
      notifyError("Open file failed");
    }
  };

  const closeFilePreviewModal = () => {
    setFilePreviewModal({
      show: false,
      title: "",
      url: "",
    });
  };

  const getUploadedFileEntries = (item, type, uploadType = "") => {
    const ep = (item && item.extendedProps) || {};
    let rows = type === "sleepstudy" ? ep.sleepstudy || [] : ep.reports || [];
    if (type === "sleepstudy" && uploadType) {
      const groups = splitSleepStudyUploads(rows);
      rows = uploadType === SCORED_SLEEP_STUDY_UPLOAD_TYPE ? groups.scored : groups.unscored;
    }
    return rows.flatMap((row) =>
      (Array.isArray(row.reportFile) ? row.reportFile : []).map((fileName) => ({
        type: type === "sleepstudy" ? getSleepStudyFileCollection(row) : type,
        row,
        fileName,
      })),
    );
  };

  const getAllUploadedFileEntries = (item) => [
    ...getUploadedFileEntries(item, "report"),
    ...getUploadedFileEntries(item, "sleepstudy"),
  ];

  const openFileModal = (item, type, label, uploadType = "") => {
    setFileModal({
      show: true,
      title: label,
      type,
      files: getUploadedFileEntries(item, type, uploadType),
    });
  };

  const openAllFilesModal = (item) => {
    setFileModal({
      show: true,
      title: "Case",
      type: "",
      files: getAllUploadedFileEntries(item),
    });
  };

  const closeFileModal = () => {
    setFileModal({
      show: false,
      title: "",
      type: "report",
      files: [],
    });
  };

  const closeEmailModal = () => {
    setEmailModal({
      show: false,
      item: null,
      emailList: [],
    });
    setSelectedEmail("");
    setSendingEmail(false);
  };

  const updateCaseEmailRows = (targetItem, formattedDate) => {
    const updateItem = (item) => {
      const sameRow =
        item.id === targetItem.id ||
        (item.ref && targetItem.ref && item.ref.path === targetItem.ref.path);

      if (!sameRow) return item;

      return {
        ...item,
        extendedProps: {
          ...(item.extendedProps || {}),
          email: true,
          lastestMail: formattedDate,
        },
      };
    };

    setCases((prev) => prev.map(updateItem));
    setSelectedCase((prev) => (prev ? updateItem(prev) : prev));
  };

  const updateCaseReportValidationClear = (targetItem, clearEntry, clearAt, savedProps) => {
    const updateItem = (item) => {
      const sameRow =
        item.id === targetItem.id ||
        (item.ref && targetItem.ref && item.ref.path === targetItem.ref.path);

      if (!sameRow) return item;

      const ep = item.extendedProps || {};
      return {
        ...item,
        extendedProps: {
          ...ep,
          ...(savedProps || buildReportValidationClearProps(ep, clearEntry, clearAt)),
          reportValidationManualClearLog: [
            ...(ep.reportValidationManualClearLog || []),
            clearEntry,
          ],
        },
      };
    };

    setCases((prev) => prev.map(updateItem));
    setSelectedCase((prev) => (prev ? updateItem(prev) : prev));
  };

  const updateCaseDiskSpaceErrorClear = (targetItem, nextExtendedProps) => {
    const updateItem = (item) => {
      const sameRow =
        item.id === targetItem.id ||
        (item.ref && targetItem.ref && item.ref.path === targetItem.ref.path);

      if (!sameRow) return item;

      return {
        ...item,
        extendedProps: {
          ...(item.extendedProps || {}),
          ...nextExtendedProps,
        },
      };
    };

    setCases((prev) => prev.map(updateItem));
    setSelectedCase((prev) => (prev ? updateItem(prev) : prev));
  };

  const handleClearReportValidationError = async (item) => {
    const ep = (item && item.extendedProps) || {};
    const reportValidationErrors = getReportValidationErrors(ep);

    if (!item || !item.ref) {
      notifyError("Missing booking data");
      return;
    }

    if (reportValidationErrors.length === 0) {
      notifyWarning("No report error to clear");
      return;
    }

    const technicianCheck = hasTechnicianReportCheckError(ep);
    const willClear = await swal({
      title: technicianCheck ? "Skip technician report check" : "Confirm manual report clear",
      text:
        `${ep.order || item.title || "-"} ${getPatientName(ep)}\n\n` +
        (technicianCheck
          ? "Skip this technician report check. On the next client loop, the case will move from problems back to the doctor's folder and return to Assigned to Doctor."
          : "Use this only after the final report/medical certificate were uploaded to BDSleep and NAS, or the case was cleared manually."),
      icon: "warning",
      buttons: ["Cancel", technicianCheck ? "Skip / Clear" : "Uploaded / Clear"],
      dangerMode: true,
      ...(technicianCheck ? { content: { element: "input", attributes: {
        placeholder: "Reason for bypassing the technician report check", type: "text",
      } } } : {}),
    });

    if (!willClear) return;
    const skipReason = technicianCheck && typeof willClear === "string" ? willClear.trim() : "";
    if (technicianCheck && !skipReason) {
      notifyWarning("Enter a reason before skipping the technician report check.");
      return;
    }

    const clearAt = Timestamp.now();
    const createBy =
      (currentUser && (currentUser.displayName || currentUser.email)) || "";
    const clearEntry = {
      action: technicianCheck ? "skip_technician_report_check" : "clear_report_validation_error",
      order: ep.order || item.title || "",
      status: ep.status || "",
      previousStatus: ep.reportValidationStatus || "",
      previousSummary: ep.reportValidationSummary || "",
      previousErrorCount: reportValidationErrors.length,
      previousErrorCodes: reportValidationErrors
        .map((error) => error && error.code)
        .filter(Boolean)
        .join(", "),
      createBy,
      createAt: clearAt,
      ...(technicianCheck ? { reason: skipReason, checkId: (ep.technicianReportCheck || {}).checkId || "" } : {}),
    };

    try {
      setClearingReportErrorId(item.id || (item.ref && item.ref.path) || "");
      let savedProps;
      if (technicianCheck) {
        savedProps = await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(item.ref);
          const current = snapshot.exists() ? snapshot.data().extendedProps || {} : {};
          if (!isSameTechnicianReportError(ep, current)) {
            throw new Error("The case or report check changed. Refresh and review the current error before clearing it.");
          }
          const props = buildReportValidationClearProps(current, clearEntry, clearAt);
          transaction.update(item.ref, {
            ...buildReportValidationClearUpdates(props),
            "extendedProps.reportValidationManualClearLog": arrayUnion(clearEntry),
          });
          return props;
        });
      } else {
        await updateDoc(item.ref, {
          ...buildReportValidationClearUpdates(buildReportValidationClearProps(ep, clearEntry, clearAt)),
          "extendedProps.reportValidationManualClearLog": arrayUnion(clearEntry),
        });
      }

      updateCaseReportValidationClear(item, clearEntry, clearAt, savedProps);
      notifyTopRight(technicianCheck ? "Technician report check skipped; waiting for client to restore case" : "Report error cleared");
    } catch (error) {
      console.error("handleClearReportValidationError error", error);
      notifyError(technicianCheck ? error.message || "Clear report error failed" : "Clear report error failed");
    } finally {
      setClearingReportErrorId("");
    }
  };

  const handleRemoveDiskSpaceError = async (item) => {
    const ep = (item && item.extendedProps) || {};

    if (!item || !item.ref) {
      notifyError("Missing booking data");
      return;
    }

    const downloadDiskSpaceError = isDiskSpaceTransferStatus(
      getDownloadStatus(ep),
    );
    const extractDiskSpaceError = isDiskSpaceTransferStatus(
      getExtractStatus(ep),
    );
    const techTransferDiskSpaceError = isDiskSpaceTransferStatus(
      getTechTransferStatus(ep),
    );
    const doctorTransferDiskSpaceError = isDiskSpaceTransferStatus(
      getDoctorTransferStatus(ep),
    );
    const reportDiskSpaceError = hasReportValidationDiskSpaceError(ep);

    if (
      !downloadDiskSpaceError &&
      !extractDiskSpaceError &&
      !techTransferDiskSpaceError &&
      !doctorTransferDiskSpaceError &&
      !reportDiskSpaceError
    ) {
      notifyWarning("No Disk Space error to remove");
      return;
    }

    const willRemove = await swal({
      title: "Confirm remove Disk Space error",
      text:
        `${ep.order || item.title || "-"} ${getPatientName(ep)}\n\n` +
        "This clears the Disk Space error and returns the affected step to retry state. It does not delete booking data or files.",
      icon: "warning",
      buttons: ["Cancel", "Remove Error"],
      dangerMode: true,
    });

    if (!willRemove) return;

    const clearAt = Timestamp.now();
    const createBy =
      (currentUser && (currentUser.displayName || currentUser.email)) || "";
    const nextExtendedProps = {
      updatedBy: createBy,
      updatedAt: clearAt,
    };
    const payload = {
      "extendedProps.updatedBy": createBy,
      "extendedProps.updatedAt": clearAt,
    };

    if (downloadDiskSpaceError) {
      Object.assign(nextExtendedProps, {
        officeDownloadStatus: "idle",
        officeDownloadError: "",
      });
      Object.assign(payload, {
        "extendedProps.officeDownloadStatus": "idle",
        "extendedProps.officeDownloadError": "",
      });
    }

    if (extractDiskSpaceError) {
      Object.assign(nextExtendedProps, {
        officeExtractStatus: "idle",
        officeExtractError: "",
      });
      Object.assign(payload, {
        "extendedProps.officeExtractStatus": "idle",
        "extendedProps.officeExtractError": "",
      });
    }

    if (techTransferDiskSpaceError) {
      Object.assign(nextExtendedProps, {
        techTransferStatus: "idle",
        techTransferError: "",
      });
      Object.assign(payload, {
        "extendedProps.techTransferStatus": "idle",
        "extendedProps.techTransferError": "",
      });
    }

    if (doctorTransferDiskSpaceError) {
      Object.assign(nextExtendedProps, {
        doctorTransferStatus: "idle",
        doctorTransferError: "",
      });
      Object.assign(payload, {
        "extendedProps.doctorTransferStatus": "idle",
        "extendedProps.doctorTransferError": "",
      });
    }

    if (reportDiskSpaceError) {
      const storedReportValidationErrors = Array.isArray(
        ep.reportValidationErrors,
      )
        ? ep.reportValidationErrors
        : [];
      const remainingReportValidationErrors = storedReportValidationErrors.filter(
        (error) => !isReportValidationDiskSpaceError(error),
      );
      const remainingBlockingErrors = remainingReportValidationErrors.filter(
        (error) => (error && error.level) !== "warning",
      );
      const reportValidationStatus =
        remainingBlockingErrors.length > 0 ? "error" : "ok";
      const reportValidationSummary =
        remainingBlockingErrors.length > 0
          ? formatReportValidationError(remainingBlockingErrors[0])
          : "";

      Object.assign(nextExtendedProps, {
        reportValidationStatus,
        reportValidationErrors: remainingReportValidationErrors,
        reportValidationSummary,
        reportValidationSource: "booking_monitor_manual_clear",
        reportValidationUpdatedAt: clearAt,
      });
      Object.assign(payload, {
        "extendedProps.reportValidationStatus": reportValidationStatus,
        "extendedProps.reportValidationErrors": remainingReportValidationErrors,
        "extendedProps.reportValidationSummary": reportValidationSummary,
        "extendedProps.reportValidationSource": "booking_monitor_manual_clear",
        "extendedProps.reportValidationUpdatedAt": clearAt,
      });
    }

    const itemKey = item.id || (item.ref && item.ref.path) || "";

    try {
      setRemovingDiskSpaceErrorId(itemKey);
      await updateDoc(item.ref, payload);
      updateCaseDiskSpaceErrorClear(item, nextExtendedProps);
      notifyTopRight("Disk Space error removed");
    } catch (error) {
      console.error("handleRemoveDiskSpaceError error", error);
      notifyError("Remove Disk Space error failed");
    } finally {
      setRemovingDiskSpaceErrorId("");
    }
  };

  const openEmailModal = async (item) => {
    const ep = (item && item.extendedProps) || {};
    const targetId = String(ep.customer || "").trim();

    if (!targetId) {
      notifyWarning("This booking has no customer");
      setEmailModal({ show: true, item, emailList: [] });
      setSelectedEmail("");
      return;
    }

    try {
      const customerSnap = await getDocs(collectionGroup(db, "customers"));
      let emailList = [];

      for (const docSnap of customerSnap.docs) {
        if (String(docSnap.id || "").trim() === targetId) {
          const rawEmailList = docSnap.data().emailList || [];
          emailList = Array.isArray(rawEmailList)
            ? rawEmailList.filter(Boolean)
            : [rawEmailList].filter(Boolean);
          break;
        }
      }

      if (emailList.length === 0) {
        notifyWarning("No email found for this customer");
      }

      setEmailModal({ show: true, item, emailList });
      setSelectedEmail(emailList[0] || "");
    } catch (error) {
      console.error("openEmailModal error", error);
      notifyError("Load customer email failed");
    }
  };

  const buildSleepStudyMail = (item, to, sendAll = false) => {
    const ep = (item && item.extendedProps) || {};
    const fullName = `${ep.firstname || ""} ${ep.lastname || ""}`.trim();
    const order = ep.order || item.title || "";
    const customer = ep.customer || "";
    const hn = ep.hn || "";
    const refHn = ep.refHn || "";
    const latestSS = ep.latestSleepStudyDate || "";
    const baseUrl = sendAll
      ? "https://study2.bdsleep.tech"
      : "https://study.bdsleep.tech";

    return {
      to,
      message: {
        subject: `Sleep Study Report ${order}`,
        html: `<p>เรียน ${customer},</p>
          <p>${order}<br>
          ${fullName}<br>
          ${refHn}<br>
          ตรวจเมื่อวันที่ ${latestSS}</p>
          <p>ดูผลได้ที่ ${baseUrl}/patient-page/${hn}<p>
          <p>ขอแสดงความนับถือ,<br>
          BDSleep</p>`,
      },
      order,
      fullName,
      refHn,
      sleepStudyDate: latestSS,
      hn,
    };
  };

  const buildDoctorAssignmentMail = (item, doctorEmail) => {
    const ep = (item && item.extendedProps) || {};
    const order = ep.order || item.title || "";
    const firstName = ep.firstname || "";
    const lastName = ep.lastname || "";
    const latestSleepStudyDate =
      ep.latestSleepStudyDate || toInputDate(item && item.start) || "";
    const createBy =
      (currentUser && (currentUser.displayName || currentUser.email)) || "";

    return {
      to: doctorEmail,
      message: {
        subject: `${order} : ${firstName} ${lastName}`.trim(),
        html: `
        <div style="border:1px solid gray; padding:1rem; background-color:#eeeeee;">
          <table cellpadding="2" cellspacing="0" style="border-collapse: collapse;">
            <tr>
              <td><strong>SleepStudy No</strong></td>
              <td>${order}</td>
            </tr>
            <tr>
              <td><strong>Full Name</strong></td>
              <td>${firstName} ${lastName}</td>
            </tr>
            <tr>
              <td><strong>Date </br>(YYYY-MM-DD)</strong></td>
              <td>${latestSleepStudyDate}</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>${ep.type || ""}</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Assigned to Doctor</td>
            </tr>
            <tr>
              <td><strong>By</strong></td>
              <td>${createBy}</td>
            </tr>
          </table>
          <hr style="border: 0; height: 1px; background: #555; margin-top: 1rem;" />
          <p style="margin-top: 1rem;">
            Best regards,<br/>
            sleepcare@braindynamics.co.th
          </p>
        </div>
      `,
      },
    };
  };

  const getDoctorEmailByName = async (doctorName) => {
    const normalizedName = String(doctorName || "").trim();
    if (!normalizedName || normalizedName === "กรุณาเลือก") return "";

    const doctorOption = doctorOptions.find(
      (doctor) => String(doctor.name || "").trim() === normalizedName,
    );

    if (doctorOption && doctorOption.email) {
      return doctorOption.email;
    }

    const doctorQuerySnap = await getDocs(
      query(collection(db, "doctors"), where("name", "==", normalizedName)),
    );
    const doctorDoc = doctorQuerySnap.docs[0];

    return doctorDoc ? doctorDoc.data().email || "" : "";
  };

  const sendDoctorAssignmentMail = async (item, doctorName) => {
    const doctorEmail = await getDoctorEmailByName(doctorName);

    if (!doctorEmail) {
      notifyWarning("Status updated, but this doctor has no email");
      return;
    }

    await setDoc(
      doc(collection(db, "mail")),
      buildDoctorAssignmentMail(item, doctorEmail),
    );
    notifyTopRight("Send doctor email success");
  };

  const handleSendBookingEmail = async (sendAll = false) => {
    const item = emailModal.item;
    const ep = (item && item.extendedProps) || {};
    const to = sendAll ? emailModal.emailList : selectedEmail;
    const hasRecipient = Array.isArray(to) ? to.length > 0 : !!to;

    if (!item || !item.ref) {
      notifyError("Missing booking data");
      return;
    }

    if (!hasRecipient) {
      notifyWarning("Please select email");
      return;
    }

    const recipientText = Array.isArray(to) ? `${to.length} email(s)` : to;
    const willSendEmail = await swal({
      title: `Are you sure to send email to ${recipientText} ?`,
      text: `${ep.order || item.title || "-"} ${getPatientName(ep)}`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    });

    if (!willSendEmail) return;

    try {
      setSendingEmail(true);
      const mailRef = doc(collection(db, "mail"));
      await setDoc(mailRef, buildSleepStudyMail(item, to, sendAll));

      const formattedDate = formatDateOnly();
      await updateDoc(item.ref, {
        "extendedProps.email": true,
        "extendedProps.lastestMail": formattedDate,
      });

      updateCaseEmailRows(item, formattedDate);
      notifyTopRight("Send email success");
      closeEmailModal();
    } catch (error) {
      console.error("handleSendBookingEmail error", error);
      notifyError("Send email failed");
      setSendingEmail(false);
    }
  };

  const renderFileSummary = (item, type, label, uploadType = "") => {
    const files = getUploadedFileEntries(item, type, uploadType);

    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-left"
        disabled={files.length === 0}
        style={{
          color: files.length ? "#5b516b" : "#9b95a8",
          fontWeight: 600,
          textDecoration: files.length ? "underline" : "none",
          cursor: files.length ? "pointer" : "default",
          whiteSpace: "nowrap",
        }}
        onClick={() => files.length && openFileModal(item, type, label, uploadType)}
      >
        {label}: {files.length ? `${files.length} file(s)` : "No file"}
      </button>
    );
  };

  const renderInlineFileList = (item, type, label, uploadType = "") => {
    const files = getUploadedFileEntries(item, type, uploadType);

    return (
      <div>
        <div className="small text-muted mb-1">
          {label}: {files.length ? `${files.length} file(s)` : "No file"}
        </div>
        {files.length > 0 && (
          <div className="d-flex flex-column" style={{ gap: 4 }}>
            {files.map(({ row, fileName }, index) => (
              <button
                key={`${type}-modal-${row.id}-${fileName}-${index}`}
                type="button"
                className="btn btn-link btn-sm p-0 text-left"
                style={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={fileName}
                onClick={() => handleOpenUploadedFile(row, type, fileName)}
              >
                {fileName}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleSave = async ({ forceManualOverride = false } = {}) => {
    if (!selectedCase || !selectedCase.ref) return;

    setSaving(true);

    const statusOrder = [
      SLEEP_TEST_SCHEDULED_STATUS,
      "Transferred To Center",
      "Assigned",
      "Technician Scored",
      "Assigned to Doctor",
      DOCTOR_SCORED_STATUS,
    ];

    const colorList = [
      "#005DEA",
      // brgiht blue
      "#fab2a2",
      "#40C4FF",
      "#00CEAB",
      // light blue
      "#9ef542",
      "#26b99a",
    ];
    const index_color = statusOrder.indexOf(editStatus);
    const color = colorList[index_color] || "red";

    try {
      const currentSnapshot = (editStatus === ASSIGNED_TO_DOCTOR_STATUS ||
        hasTechnicianCheckDoctorCopy(selectedCase.extendedProps || {}) ||
        getAssignedDoctor(selectedCase.extendedProps || {}) !== editDoctor)
        ? await getDoc(selectedCase.ref) : null;
      const currentExtendedProps = {
        ...(selectedCase.extendedProps || {}),
        ...(currentSnapshot && currentSnapshot.exists()
          ? currentSnapshot.data().extendedProps || {} : {}),
      };
      if (editStatus === ASSIGNED_TO_DOCTOR_STATUS && currentExtendedProps.hn) {
        currentExtendedProps.sleepstudy = await loadPatientSleepStudyFiles(String(currentExtendedProps.hn));
        setSelectedCase((previous) => ({ ...previous, extendedProps: currentExtendedProps }));
      }
      // The booking activity trigger runs without the browser auth context,
      // so persist the actual operator without replacing the booking creator.
      const activityUpdatedBy =
        (currentUser && (currentUser.displayName || currentUser.email)) ||
        "Unknown user";
      const activityUpdatedAt = Timestamp.now();
      const shouldSendDoctorAssignmentMail =
        editStatus === ASSIGNED_TO_DOCTOR_STATUS &&
        (currentExtendedProps.status !== ASSIGNED_TO_DOCTOR_STATUS ||
          getAssignedDoctor(currentExtendedProps) !== editDoctor);
      const blockReason = getMonitorSaveBlockReason({
        currentExtendedProps,
        nextStatus: editStatus,
        nextTechnician: editTechnician,
        nextDoctor: editDoctor,
        bookingPath: selectedCase.ref.path,
      });
      if (hasTechnicianCheckDoctorCopy(currentExtendedProps) &&
          getAssignedDoctor(currentExtendedProps) !== editDoctor) {
        notifyWarning("The doctor already has this case, including any copy in problems. Restore or recover that copy before reassignment.");
        return;
      }
      const confirmsRawMultiFileReview =
        forceManualOverride &&
        currentExtendedProps.officeExtractStatus === REVIEW_REQUIRED_STATUS &&
        editStatus === "Assigned";
      const confirmsScoredMultiFileReview =
        forceManualOverride &&
        isScoredCaseReviewRequired(currentExtendedProps) &&
        editStatus === ASSIGNED_TO_DOCTOR_STATUS;
      const confirmsMultiFileReview =
        confirmsRawMultiFileReview || confirmsScoredMultiFileReview;
      let manualOverrideEntry = null;

      if (forceManualOverride && editStatus === ASSIGNED_TO_DOCTOR_STATUS && (
        editDoctor === "กรุณาเลือก" ||
        getPendingScoredUploadPaths(currentExtendedProps, selectedCase.ref.path).length ||
        (!confirmsScoredMultiFileReview && !isScoredCaseReadyForDoctor(currentExtendedProps, selectedCase.ref.path))
      )) {
        notifyWarning("Select a doctor and wait for scored files before completing the handoff.");
        return;
      }

      if (blockReason) {
        const lockLogLines = getManualStatusLockLog({
          ep: currentExtendedProps,
          reason: blockReason,
          nextStatus: editStatus,
          nextTechnician: editTechnician,
          nextDoctor: editDoctor,
        });

        if (!forceManualOverride) {
          setManualOverrideModal({
            show: true,
            reason: blockReason,
            log: lockLogLines,
          });
          return;
        }

        manualOverrideEntry = {
          reason: blockReason,
          log: lockLogLines,
          fromStatus: currentExtendedProps.status || "",
          toStatus: editStatus,
          fromTechnician: getAssignedTech(currentExtendedProps),
          toTechnician: editTechnician,
          fromDoctor: getAssignedDoctor(currentExtendedProps),
          toDoctor: editDoctor,
          multiFileReviewConfirmed: confirmsMultiFileReview,
          ...(confirmsScoredMultiFileReview ? {
            scoredSourcePath: reviewedScoredSourcePath,
            scoredSourceIds: getScoredCaseSourceChoices(currentExtendedProps).map((source) => source.id).sort(),
          } : {}),
          createBy: activityUpdatedBy,
          createAt: Timestamp.now(),
        };
      }

      const currentLatestSleepStudyDate = toInputDate(
        currentExtendedProps.latestSleepStudyDate || selectedCase.start,
      );
      const latestSleepStudyDateChanged =
        editLatestSleepStudyDate !== currentLatestSleepStudyDate;

      if (latestSleepStudyDateChanged) {
        const willChangeLatestSleepStudyDate = await swal({
          title: "Confirm Latest Sleep Study Date Change",
          text:
            `${currentExtendedProps.order || selectedCase.title || "-"}\n` +
            `${getPatientName(currentExtendedProps)}\n\n` +
            `Current: ${currentLatestSleepStudyDate || "Not set"}\n` +
            `New: ${editLatestSleepStudyDate || "Not set"}\n\n` +
            "This changes the event's latest sleep study date and its monitor deadline.",
          icon: "warning",
          buttons: ["Review Event", "Confirm Change"],
          dangerMode: true,
        });

        if (!willChangeLatestSleepStudyDate) return;
      }

      const payload = {
        "extendedProps.status": editStatus,
        "extendedProps.technician": editTechnician,
        "extendedProps.doctor": editDoctor,
        "extendedProps.note": editNote,
        "extendedProps.updatedBy": activityUpdatedBy,
        "extendedProps.updatedAt": activityUpdatedAt,
      };

      if (latestSleepStudyDateChanged) {
        payload["extendedProps.latestSleepStudyDate"] =
          editLatestSleepStudyDate;
      }

      const reviewCompletionProps = editStatus === ASSIGNED_TO_DOCTOR_STATUS
        ? buildReturnedScoredCaseProps(currentExtendedProps, selectedCase.ref.path) : {};
      Object.entries(reviewCompletionProps).forEach(([key, value]) => {
        payload[`extendedProps.${key}`] = value;
      });
      if (confirmsMultiFileReview) {
        const reviewedAt = Timestamp.now();
        const reviewedBy =
          (currentUser && (currentUser.displayName || currentUser.email)) || "";

        if (confirmsRawMultiFileReview) {
          payload["extendedProps.officeExtractStatus"] = "done";
          payload["extendedProps.officeExtractError"] = "";
          payload["extendedProps.officeMultiFileReviewStatus"] = "reviewed";
          payload["extendedProps.officeMultiFileReviewedAt"] = reviewedAt;
          payload["extendedProps.officeMultiFileReviewedBy"] = reviewedBy;
          Object.assign(reviewCompletionProps, {
            officeExtractStatus: "done",
            officeExtractError: "",
            officeMultiFileReviewStatus: "reviewed",
            officeMultiFileReviewedAt: reviewedAt,
            officeMultiFileReviewedBy: reviewedBy,
          });
        }

        if (confirmsScoredMultiFileReview) {
          const scoredReviewProps = buildScoredCaseReviewProps(
            currentExtendedProps, reviewedScoredSourcePath, reviewedBy, reviewedAt, selectedCase.ref.path,
          );
          Object.entries(scoredReviewProps).forEach(([key, value]) => {
            payload[`extendedProps.${key}`] = value;
          });
          Object.assign(reviewCompletionProps, scoredReviewProps);
        }
      }

      if (manualOverrideEntry) {
        payload["extendedProps.monitorManualOverrideLog"] =
          arrayUnion(manualOverrideEntry);
      }

      let nextStart = null;
      if (editStartDate) {
        nextStart = new Date(editStartDate);
        payload.start = Timestamp.fromDate(nextStart);
        payload.color = color;
      }

      const shouldMarkDirectScoredUploads =
        isDirectTechnicianScoredTransition({
          currentExtendedProps,
          nextStatus: editStatus,
        }) && Boolean(currentExtendedProps.hn);
      let directScoredUploadDocs = [];

      if (shouldMarkDirectScoredUploads) {
        const uploadSnapshot = await getDocs(
          query(
            collection(db, "patients", String(currentExtendedProps.hn), "sleepstudy"),
            where(
              BOOKING_UPLOAD_BOOKING_PATH_FIELD,
              "==",
              selectedCase.ref.path,
            ),
          ),
        );
        directScoredUploadDocs = uploadSnapshot.docs.filter((uploadDoc) =>
          isUnmarkedSleepstudyZipUpload(uploadDoc.data() || {}),
        );
      }

      if (directScoredUploadDocs.length > 0) {
        const batch = writeBatch(db);
        batch.update(selectedCase.ref, payload);
        directScoredUploadDocs.forEach((uploadDoc) => {
          batch.update(uploadDoc.ref, {
            scoreUploadType: "technician_scored",
            scoreUploadSource: "booking_monitor",
          });
        });
        await batch.commit();
      } else {
        await updateDoc(selectedCase.ref, payload);
      }

      const updatedSelectedCase = {
        ...selectedCase,
        start: nextStart || selectedCase.start,
        extendedProps: {
          ...currentExtendedProps,
          status: editStatus,
          technician: editTechnician,
          doctor: editDoctor,
          note: editNote,
          updatedBy: activityUpdatedBy,
          updatedAt: activityUpdatedAt,
          ...(latestSleepStudyDateChanged
            ? { latestSleepStudyDate: editLatestSleepStudyDate }
            : {}),
          ...(directScoredUploadDocs.length > 0
            ? {
                sleepstudy: (currentExtendedProps.sleepstudy || []).map((upload) =>
                  directScoredUploadDocs.some(
                    (uploadDoc) => uploadDoc.id === upload.id,
                  )
                    ? {
                        ...upload,
                        scoreUploadType: "technician_scored",
                        scoreUploadSource: "booking_monitor",
                      }
                    : upload,
                ),
              }
            : {}),
          ...reviewCompletionProps,
          ...(manualOverrideEntry
            ? {
                monitorManualOverrideLog: [
                  ...(currentExtendedProps.monitorManualOverrideLog || []),
                  manualOverrideEntry,
                ],
              }
            : {}),
        },
      };

      setCases((prev) =>
        prev.map((item) => {
          const sameRow =
            item.id === selectedCase.id ||
            (item.ref &&
              selectedCase.ref &&
              item.ref.path === selectedCase.ref.path);

          if (!sameRow) return item;

          return {
            ...item,
            start: nextStart || item.start,
            extendedProps: {
              ...(item.extendedProps || {}),
              status: editStatus,
              technician: editTechnician,
              doctor: editDoctor,
              note: editNote,
              updatedBy: activityUpdatedBy,
              updatedAt: activityUpdatedAt,
              ...(latestSleepStudyDateChanged
                ? { latestSleepStudyDate: editLatestSleepStudyDate }
                : {}),
              ...reviewCompletionProps,
              ...(manualOverrideEntry
                ? {
                    monitorManualOverrideLog: [
                      ...((item.extendedProps || {})
                        .monitorManualOverrideLog || []),
                      manualOverrideEntry,
                    ],
                  }
                : {}),
            },
          };
        }),
      );

      if (shouldSendDoctorAssignmentMail) {
        try {
          await sendDoctorAssignmentMail(updatedSelectedCase, editDoctor);
        } catch (mailError) {
          console.error("sendDoctorAssignmentMail error", mailError);
          notifyWarning("Status updated, but send doctor email failed");
        }
      }

      handleCloseModal();
    } catch (error) {
      console.error("handleSave error", error);
      notifyError(error.message || "Unable to save the case.");
    } finally {
      setSaving(false);
    }
  };

  const closeManualOverrideModal = () => {
    setManualOverrideModal({ show: false, reason: "", log: [] });
  };

  const confirmManualOverride = () => {
    closeManualOverrideModal();
    handleSave({ forceManualOverride: true });
  };

  const formatAssigneeLabel = (row) => {
    if (!row) return "-";
    const name = row.name || "";
    const nickname = row.nickname || "";
    if (!nickname || nickname === name) return name;
    return `${name} (${nickname})`;
  };

  return (
    <Fragment>
      <style>
        {`
          .sleep-study-summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
            gap: 10px;
            margin-bottom: 14px;
          }

          @media (min-width: 1500px) {
            .sleep-study-summary-grid {
              grid-template-columns: repeat(7, minmax(0, 1fr));
            }
          }

          .sleep-study-summary-card {
            border-width: 1px 1px 1px 4px;
            border-style: solid;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(22, 28, 45, 0.05) !important;
          }

          .sleep-study-summary-card .card-body {
            min-height: 82px;
            padding: 12px 14px;
            gap: 10px;
          }

          .sleep-study-summary-copy {
            min-width: 0;
          }

          .sleep-study-summary-title {
            color: #6b6480;
            font-size: 0.75rem;
            font-weight: 600;
            line-height: 1.2;
            margin-bottom: 6px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .sleep-study-summary-subtitle {
            color: #2f2a3d;
            font-size: 0.72rem;
            line-height: 1.25;
            white-space: normal;
          }

          .sleep-study-summary-value {
            color: #5f6368;
            flex: 0 0 auto;
            font-size: 2rem;
            font-weight: 600;
            line-height: 1;
            text-align: right;
          }

          .sleep-study-monitor-table th,
          .sleep-study-monitor-table td {
            padding: 0.32rem 0.42rem;
            font-size: 0.78rem;
            line-height: 1.15;
            vertical-align: middle;
          }

          .sleep-study-monitor-table .badge {
            font-size: 0.66rem;
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-warning > td:first-child {
            border-left: 4px solid #f0ad4e;
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-danger > td:first-child {
            border-left: 4px solid #dc3545;
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-warning > td {
            background-color: rgba(240, 173, 78, 0.08);
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-danger > td {
            background-color: rgba(220, 53, 69, 0.08);
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-warning:hover > td {
            background-color: rgba(240, 173, 78, 0.14);
          }

          .sleep-study-monitor-table tbody tr.monitor-row-deadline-danger:hover > td {
            background-color: rgba(220, 53, 69, 0.14);
          }

          .monitor-case-cell {
            max-width: 300px;
            min-width: 0;
          }

          .monitor-case-top,
          .monitor-case-meta,
          .monitor-case-patient-line {
            display: flex;
            align-items: center;
            min-width: 0;
          }

          .monitor-case-top {
            gap: 5px;
          }

          .monitor-case-order {
            color: #3f3a4d;
            font-size: 0.84rem;
            font-weight: 700;
            line-height: 1.15;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .monitor-type-badge {
            background: #e9ecef;
            border-radius: 5px;
            color: #2f2d36;
            flex: 0 0 auto;
            font-size: 0.62rem;
            font-weight: 700;
            line-height: 1;
            padding: 3px 5px;
          }

          .monitor-case-icon {
            flex: 0 0 auto;
            height: 13px;
            width: 13px;
          }

          .monitor-case-meta {
            color: #7d7890;
            font-size: 0.72rem;
            gap: 6px;
            line-height: 1.15;
            margin-top: 2px;
            white-space: nowrap;
          }

          .monitor-case-date {
            color: #6f6980;
            flex: 0 0 auto;
          }

          .monitor-case-deadline {
            font-weight: 800 !important;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .monitor-case-patient-line {
            gap: 6px;
            margin-top: 2px;
          }

          .monitor-case-patient {
            color: #4f4a5d;
            font-size: 0.78rem;
            font-weight: 700;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .monitor-case-hn {
            color: #7d7890;
            flex: 0 0 auto;
            font-size: 0.7rem;
            white-space: nowrap;
          }

          .monitor-case-note {
            color: #8a8599;
            font-size: 0.7rem;
            line-height: 1.15;
            margin-top: 2px;
            max-width: 280px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .monitor-actions {
            display: flex;
            gap: 5px;
            justify-content: flex-end;
          }

          .monitor-actions .btn {
            border-radius: 7px;
            font-size: 0.72rem;
            font-weight: 700;
            line-height: 1.1;
            min-width: 60px;
            padding: 0.28rem 0.42rem;
          }

          .sleep-study-issue-card .card-body {
            padding: 12px 16px;
          }

          .sleep-study-issue-table-wrap {
            max-height: 520px;
            overflow-y: auto;
          }

          .sleep-study-issue-table {
            table-layout: fixed;
            min-width: 1280px;
          }

          .sleep-study-issue-table th,
          .sleep-study-issue-table td {
            padding: 0.35rem 0.45rem;
            font-size: 0.78rem;
            line-height: 1.2;
            vertical-align: middle;
          }

          .sleep-study-issue-table .issue-text-clip {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .sleep-study-issue-table .issue-col-case {
            width: 165px;
          }

          .sleep-study-issue-table .issue-col-zip {
            width: 150px;
          }

          .sleep-study-issue-table .issue-col-workflow {
            width: 132px;
          }

          .sleep-study-issue-table .issue-workflow-badge {
            align-items: center;
            justify-content: center;
            max-width: 126px;
            min-height: 32px;
            text-align: center;
            white-space: normal;
            word-break: normal;
          }

          .sleep-study-issue-table .issue-col-files {
            width: 155px;
          }

          .sleep-study-issue-table .issue-col-detail {
            width: 360px;
          }

          .sleep-study-issue-table .issue-detail-text {
            display: block;
            white-space: normal;
            word-break: break-word;
            max-height: 4.8em;
            overflow-y: auto;
          }

          .sleep-study-issue-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f8f9fa;
          }

          .sleep-study-issue-table .badge {
            font-size: 0.68rem;
            line-height: 1;
            padding: 0.22rem 0.4rem;
          }

          .sleep-study-issue-table .btn-sm {
            padding: 0.15rem 0.4rem;
            font-size: 0.72rem;
            line-height: 1.2;
          }
        `}
      </style>
      <input
        ref={reportInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUploadChange(e, "report")}
      />
      <input
        ref={sleepStudyInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUploadChange(e, "sleepstudy")}
      />
      <Row>
        <Col xl={12}>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="mb-1">Sleep Study Workflow Monitor</h3>
              <div className="text-muted">
                Monitor center download/extract, technician transfer, doctor
                transfer, and case assignment.
              </div>
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              <Button
                as={Link}
                to="/sleep-study-monitor-flow"
                variant="outline-secondary"
              >
                Sleep Study Guide
              </Button>
              <Button variant="outline-primary" onClick={loadCases}>
                Refresh
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      <div className="sleep-study-summary-grid">
        <SummaryCard
          title="Transferred To Center"
          value={counts.transferred.total}
          subtitle={`DL ${counts.transferred.downloaded} | EX ${counts.transferred.extracted} | Wait ${counts.transferred.waitingUpload}`}
          accent="#6f42c1"
        />
        <SummaryCard
          title="Assigned"
          value={counts.assigned}
          subtitle={`Need Tech ${counts.needTech} | Ready ${counts.techReady}`}
          accent="#fd7e14"
        />
        <SummaryCard
          title="Technician Scored"
          value={counts.technicianScored}
          subtitle={`Returned ${counts.techReturned} | Self ${counts.techSelfScored}`}
          accent="#495057"
        />
        <SummaryCard
          title="Assigned to Doctor"
          value={counts.assignedToDoctor}
          subtitle={`Need Doctor ${counts.needDoctor} | Ready ${counts.doctorReady}`}
          accent="#0d6efd"
        />
        <SummaryCard
          title="Tech Copied"
          value={counts.techCopied}
          subtitle="On technician client"
          accent="#ff6b35"
        />
        <SummaryCard
          title="Doctor Copied"
          value={counts.doctorCopied}
          subtitle="On doctor client"
          accent="#17a2b8"
        />
        <SummaryCard
          title="All Issues"
          value={issueFilterCount}
          subtitle={`Review ${counts.reviewRequired} | DL ${counts.downloadErrors} | EX ${counts.extractErrors} | TR ${counts.transferErrors} | RP ${counts.reportErrors}`}
          accent="#dc3545"
        />
      </div>
      <Row className="mb-1">
        <Col xl={12}>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="small text-muted mr-2 mb-2">Quick filters</span>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={quickFilter === "all" ? "primary" : "light"}
              onClick={() => setQuickFilter("all")}
            >
              All
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={getAttentionQuickFilterVariant(
                quickFilter === "issues",
                issueFilterCount,
                "danger",
                "outline-danger",
              )}
              onClick={() => setQuickFilter("issues")}
            >
              Issues
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={getAttentionQuickFilterVariant(
                quickFilter === "waiting-upload",
                counts.waitingUpload,
                "secondary",
                "outline-warning",
              )}
              onClick={() => setQuickFilter("waiting-upload")}
            >
              {formatQuickFilterLabel("Waiting Upload", counts.waitingUpload)}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={quickFilter === "need-tech" ? "warning" : "light"}
              onClick={() => setQuickFilter("need-tech")}
            >
              {formatQuickFilterLabel("Need Tech", counts.needTech)}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={
                quickFilter === "waiting-tech-copy" ? "warning" : "light"
              }
              onClick={() => setQuickFilter("waiting-tech-copy")}
            >
              {formatQuickFilterLabel("Tech Ready", counts.techReady)}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={
                quickFilter === "waiting-tech-return" ? "secondary" : "light"
              }
              onClick={() => setQuickFilter("waiting-tech-return")}
            >
              {formatQuickFilterLabel(
                "Wait Tech Return",
                counts.waitingTechReturn,
              )}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={quickFilter === "need-doctor" ? "info" : "light"}
              onClick={() => setQuickFilter("need-doctor")}
            >
              {formatQuickFilterLabel("Need Doctor", counts.needDoctor)}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={quickFilter === "waiting-doctor-copy" ? "info" : "light"}
              onClick={() => setQuickFilter("waiting-doctor-copy")}
            >
              {formatQuickFilterLabel("Doctor Ready", counts.doctorReady)}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={getAttentionQuickFilterVariant(
                quickFilter === "needs-review",
                counts.reviewRequired,
                "warning",
                "outline-warning",
              )}
              onClick={() => setQuickFilter("needs-review")}
            >
              {formatQuickFilterLabel(
                "Review Required",
                counts.reviewRequired,
              )}
            </Button>

            <Button
              size="sm"
              className="mr-2 mb-2"
              variant={getAttentionQuickFilterVariant(
                quickFilter === "report-errors",
                counts.reportErrors,
                "danger",
                "outline-danger",
              )}
              onClick={() => setQuickFilter("report-errors")}
            >
              {formatQuickFilterLabel("Report Error", counts.reportErrors)}
            </Button>
          </div>
        </Col>
      </Row>
      <Row className="g-3 mb-0">
        <Col xl={12}>
          <Card className="shadow-sm border-danger sleep-study-issue-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-bold text-danger">Issue Cases</div>
                <div className="small text-muted">
                  {issueCases.length} case(s)
                </div>
              </div>
              <div className="table-responsive sleep-study-issue-table-wrap">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 sleep-study-issue-table"
                >
                  <thead className="table-light">
                    <tr>
                      <th className="issue-col-case">Case</th>
                      <th className="issue-col-workflow">WORKFLOW</th>
                      <th>Type</th>
                      <th>TECH RECORD</th>
                      <th>Doctor Score</th>
                      <th className="issue-col-zip">ZIP / Local ZIP</th>
                      <th className="issue-col-detail">Detail</th>
                      <th className="issue-col-files">Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueCases.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted">
                          No issue case
                        </td>
                      </tr>
                    ) : (
                      issueCases.map((row) => (
                        <tr key={row.id}>
                          <td className="issue-col-case">
                            <span
                              className="issue-text-clip font-weight-bold"
                              title={row.order}
                            >
                              {row.order}
                            </span>
                            <span
                              className="issue-text-clip text-muted mt-1"
                              title={row.patientName}
                            >
                              {row.patientName}
                            </span>
                          </td>
                          <td className="issue-col-workflow">
                            <span
                              className="badge issue-workflow-badge"
                              style={{
                                ...compactBadgeStyle,
                                lineHeight: 1.15,
                                whiteSpace: "normal",
                                backgroundColor:
                                  STATUS_VARIANT[row.workflow] || "#6c757d",
                                color:
                                  row.workflow === "Assigned to Doctor"
                                    ? "#212529"
                                    : "#fff",
                              }}
                            >
                              {row.workflow}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge badge-${
                                row.errorType === "Download"
                                  ? "danger"
                                  : row.errorType === "Review Required"
                                    ? "warning"
                                  : row.errorType === "Report"
                                    ? "danger"
                                  : row.errorType === "Extract"
                                    ? "warning"
                                    : row.errorType === "Disk Space" ||
                                        row.errorType === "Client Disk Space" ||
                                        row.errorType === "Center Disk Space"
                                      ? "danger"
                                      : row.errorType.includes("Transfer")
                                        ? "danger"
                                        : "secondary"
                              }`}
                            >
                              {row.errorType}
                            </span>
                          </td>
                          <td>{row.techrecord}</td>
                          <td>{row.doctorScore}</td>
                          <td className="issue-col-zip">
                            <span
                              className="issue-text-clip"
                              title={`ZIP: ${row.zipName}`}
                            >
                              ZIP: {row.zipName}
                            </span>
                            <span
                              className="issue-text-clip text-muted mt-1"
                              title={`Local: ${row.localZipName}`}
                            >
                              Local: {row.localZipName}
                            </span>
                          </td>
                          <td
                            className={`issue-col-detail ${
                              row.errorType === "Waiting Upload"
                                ? "text-muted"
                                : row.errorType === "Review Required"
                                  ? "text-warning"
                                : "text-danger"
                            }`}
                          >
                            <span
                              className="issue-detail-text"
                              title={row.errorText || "-"}
                            >
                              {row.errorText || "-"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-column" style={{ gap: 4 }}>
                              {renderFileSummary(row.item, "report", "Report")}
                              {renderFileSummary(
                                row.item,
                                "sleepstudy",
                                "Unscored Sleep Study",
                                UNSCORED_SLEEP_STUDY_UPLOAD_TYPE,
                              )}
                              {renderFileSummary(
                                row.item,
                                "sleepstudy",
                                "Scored Sleep Study",
                                SCORED_SLEEP_STUDY_UPLOAD_TYPE,
                              )}
                              <Button
                                size="sm"
                                variant="outline-info"
                                disabled={!!uploadingType}
                                onClick={() =>
                                  handleUploadClick(row.item, "report")
                                }
                              >
                                {uploadingType === "report"
                                  ? "Uploading..."
                                  : "Upload RPT"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={!!uploadingType}
                                onClick={() =>
                                  handleUploadClick(row.item, "sleepstudy")
                                }
                              >
                                {uploadingType === "sleepstudy"
                                  ? "Uploading..."
                                  : "Upload Unscored SS"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                disabled={!!uploadingType}
                                onClick={() => handleUploadClick(row.item, "sleepstudy", SCORED_SLEEP_STUDY_UPLOAD_TYPE)}
                              >
                                {uploadingType === "sleepstudy" ? "Uploading..." : "Upload Scored SS"}
                              </Button>
                              {row.canRemoveDiskSpaceError ? (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  disabled={
                                    removingDiskSpaceErrorId ===
                                    (row.item.id ||
                                      (row.item.ref && row.item.ref.path))
                                  }
                                  onClick={() =>
                                    handleRemoveDiskSpaceError(row.item)
                                  }
                                >
                                  {removingDiskSpaceErrorId ===
                                  (row.item.id ||
                                    (row.item.ref && row.item.ref.path))
                                    ? "Removing..."
                                    : "Remove Error"}
                                </Button>
                              ) : row.canClearReportError ? (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  disabled={
                                    clearingReportErrorId ===
                                    (row.item.id ||
                                      (row.item.ref && row.item.ref.path))
                                  }
                                  onClick={() =>
                                    handleClearReportValidationError(row.item)
                                  }
                                >
                                  {clearingReportErrorId ===
                                  (row.item.id ||
                                    (row.item.ref && row.item.ref.path))
                                    ? "Clearing..."
                                    : "Clear Error"}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col xl={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
              <div
                className="d-flex flex-wrap align-items-center"
                style={{ gap: 16 }}
              >
                <div style={{ flex: "1 1 620px", minWidth: 360 }}>
                  <input
                    className="form-control"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search patient, HN, order, insurance, note, tech, doctor, transfer status"
                    style={{ minWidth: 0 }}
                  />
                </div>
                <div style={{ flex: "0 1 340px", minWidth: 280 }}>
                  <select
                    className="form-control"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        Sort: {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "0 0 220px" }}>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    {QUERY_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ flex: "0 1 376px", minWidth: 0, gap: 16 }}
                >
                  <select
                    className="form-control"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    style={{ flex: "1 1 170px", minWidth: 0, width: "auto" }}
                  >
                    <option value="All">All Email</option>
                    <option value="Sent">Sent Email</option>
                    <option value="NotSent">Not Sent</option>
                  </select>
                  <select
                    className="form-control"
                    value={nodeFilter}
                    onChange={(e) => setNodeFilter(e.target.value)}
                    style={{ flex: "1 1 190px", minWidth: 0, width: "auto" }}
                  >
                    <option value="All">All Node</option>
                    {nodeFilterOptions.map((nodeName) => (
                      <option key={nodeName} value={nodeName}>
                        {nodeName}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className="d-flex"
                  style={{ flex: "1 1 100%", minWidth: 0 }}
                >
                  {!loading && nodeCaseCounts.length > 0 && (
                    <div
                      style={{
                        flex: "1 1 280px",
                        minWidth: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                        gap: 6,
                        maxHeight: 118,
                        overflowY: "auto",
                        padding: 8,
                        backgroundColor: "#f8f9fb",
                        border: "1px solid #e5e9ef",
                        borderRadius: 6,
                      }}
                      aria-label="Case counts by node"
                      tabIndex={0}
                    >
                      {nodeCaseCounts.map(({ nodeName, nodeCode, count }) => (
                        <span
                          key={nodeName}
                          title={`${nodeName} (${nodeCode}): ${count} cases`}
                          aria-label={`${nodeName}: ${count} cases`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 6,
                            minWidth: 0,
                            padding: "4px 6px",
                            fontSize: 12,
                            lineHeight: "20px",
                            borderRadius: 4,
                            border: "2px solid #9bbbe5",
                            backgroundColor: nodeFilter === nodeName ? "#edf4ff" : "#fff",
                            color: "#334155",
                          }}
                        >
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                            {nodeCode}
                          </span>
                          <strong style={{ flexShrink: 0, minWidth: 22, padding: "0 4px", textAlign: "center", borderRadius: 3, backgroundColor: "#eaf0f6", fontVariantNumeric: "tabular-nums" }}>
                            {count}
                          </strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className="small text-muted"
                  style={{
                    marginLeft: "auto",
                    whiteSpace: "nowrap",
                    paddingLeft: 8,
                  }}
                >
                  <div className="small text-muted">
                    Showing{" "}
                    {filteredCases.length === 0
                      ? 0
                      : (currentPage - 1) * PER_PAGE + 1}
                    {" - "}
                    {Math.min(currentPage * PER_PAGE, filteredCases.length)}
                    {" of "}
                    {filteredCases.length}
                  </div>
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="table-responsive">
                <Table
                  hover
                  bordered
                  size="sm"
                  align="middle"
                  className="mb-0 sleep-study-monitor-table"
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 300 }}>Case</th>
                      <th style={{ minWidth: 250 }}>Status</th>
                      <th style={{ minWidth: 260 }}>People</th>
                      <th style={{ minWidth: 280 }}>Transfers</th>
                      <th style={{ width: 135, minWidth: 135 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center py-4 text-muted"
                        >
                          Loading cases...
                        </td>
                      </tr>
                    ) : paginatedCases.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center py-4 text-muted"
                        >
                          No cases found.
                        </td>
                      </tr>
                    ) : (
                      paginatedCases.map((item) => {
                        const ep = item.extendedProps || {};
                        const patientName = getPatientName(ep);
                        const downloadStatus = getDownloadStatus(ep);
                        const extractStatus = getExtractStatus(ep);
                        const scoredStatus = getScoredCaseStatus(ep, item.ref ? item.ref.path : "");
                        const techTransfer = getTechTransferStatus(ep);
                        const doctorTransfer = getDoctorTransferStatus(ep);
                        const reportValidationErrors =
                          getReportValidationErrors(ep);
                        const needsMultiFileReview =
                          hasMultiFileReviewRequired(ep);
                        const assignedTech = getAssignedTech(ep);
                        const assignedDoctor = getAssignedDoctor(ep);
                        const typeSymbol = getTypeSymbol(ep.type);
                        const fileCounts = getMonitorFileCounts(ep);
                        const latestSleepStudyDateText =
                          getSleepStudyMonitorDisplayDate(item);
                        const deadlineInfo = getSleepStudyDeadlineInfo(
                          { ...ep, start: item.start },
                          nodeInfo,
                        );

                        const downloadText = mapDownloadText(downloadStatus);
                        const extractText = mapExtractText(extractStatus);
                        const isSelfPay = ep.healthInsurance === "ชำระเงินเอง";
                        const isHomeSleepStudy = typeSymbol === "[HN]";

                        const rowClass =
                          downloadStatus === "failed" ||
                          downloadStatus === "disk_space_error" ||
                          extractStatus === "error" ||
                          extractStatus === "disk_space_error" ||
                          ["failed", "error", "partial_error"].includes(scoredStatus) ||
                          techTransfer === "error" ||
                          isDiskSpaceTransferStatus(techTransfer) ||
                          doctorTransfer === "error" ||
                          isDiskSpaceTransferStatus(doctorTransfer) ||
                          reportValidationErrors.length > 0
                            ? "table-danger"
                            : needsMultiFileReview
                              ? "table-warning"
                            : downloadStatus === "waiting_upload"
                              ? "table-secondary"
                              : "";
                        const deadlineRowClass =
                          deadlineInfo && deadlineInfo.level !== "normal"
                            ? `monitor-row-deadline-${deadlineInfo.level}`
                            : "";
                        const hasNote = !!String(ep.note || "").trim();

                        return (
                          <tr
                            key={item.id}
                            className={[rowClass, deadlineRowClass]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <td className="monitor-case-cell">
                              <div className="monitor-case-top">
                                <span
                                  className="monitor-case-order"
                                  title={ep.order || item.title || "-"}
                                >
                                  {ep.order || item.title || "-"}
                                </span>
                                {typeSymbol && (
                                  <span className="monitor-type-badge">
                                    {typeSymbol.replace("[", "").replace("]", "")}
                                  </span>
                                )}
                                {ep.email && (
                                  <span
                                    className="badge badge-success"
                                    title={`Sent email${
                                      ep.lastestMail ? `: ${ep.lastestMail}` : ""
                                    }`}
                                  >
                                    Mail
                                  </span>
                                )}
                                {reportValidationErrors.length > 0 && (
                                  <span
                                    className="badge badge-danger"
                                    title={formatReportValidationError(
                                      reportValidationErrors[0],
                                    )}
                                  >
                                    {getReportValidationIssueType(
                                      reportValidationErrors,
                                    ) === "Disk Space"
                                      ? "Disk Space Error"
                                      : "Report Error"}
                                  </span>
                                )}
                                {ep.technicianReportCheck && ["passed", "skipped"].includes(ep.technicianReportCheck.status) && (
                                  <span className={`badge badge-${ep.technicianReportCheck.status === "passed" ? "success" : "secondary"}`}>
                                    Tech Report: {ep.technicianReportCheck.status === "passed" ? "Passed" :
                                      ep.technicianReportCheck.released ? "Skipped" : "Restore pending"}
                                  </span>
                                )}
                                {needsMultiFileReview && (
                                  <span
                                    className="badge badge-warning"
                                    title={
                                      ep.officeScoreExtractError ||
                                      ep.officeExtractError ||
                                      "Admin must review/merge multiple files before changing status."
                                    }
                                  >
                                    {isScoredCaseReviewRequired(ep) ? "Review Scored Sources" : "Review Files"}
                                  </span>
                                )}
                                {scoredStatus !== "idle" && !isScoredCaseReviewRequired(ep) && (
                                  <span
                                    className={`badge badge-${EXTRACT_VARIANT[scoredStatus] || DOWNLOAD_VARIANT[scoredStatus] || "secondary"}`}
                                    title={ep.officeScoreDownloadError || ep.officeScoreExtractError || "Scored files received from a client return or an outside upload."}
                                  >
                                    Scored: {mapScoredCaseText(scoredStatus)}
                                  </span>
                                )}
                                {isSelfPay && (
                                  <img
                                    className="monitor-case-icon ml-auto"
                                    src={moneyBag}
                                    alt="ชำระเงินเอง"
                                    title="ชำระเงินเอง"
                                  />
                                )}
                                {isHomeSleepStudy && (
                                  <img
                                    className="monitor-case-icon"
                                    src={house}
                                    alt="Home Sleep Study"
                                    title="Home Sleep Study"
                                  />
                                )}
                              </div>

                              <div className="monitor-case-meta">
                                <span className="monitor-case-date">
                                  {latestSleepStudyDateText}
                                </span>
                                {deadlineInfo && (
                                  <span
                                    className={`monitor-case-deadline fw-semibold ${
                                      DEADLINE_TEXT_CLASS[
                                        deadlineInfo.level
                                      ] || "text-muted"
                                    }`}
                                    title={`Due ${deadlineInfo.dueDays} days after latest sleep study date`}
                                  >
                                    {deadlineInfo.text.replace("Deadline: ", "")}
                                  </span>
                                )}
                              </div>

                              <div className="monitor-case-patient-line">
                                <span
                                  className="monitor-case-patient"
                                  title={patientName}
                                >
                                  {patientName}
                                </span>
                                <span
                                  className="monitor-case-hn"
                                  title={ep.healthInsurance || "-"}
                                >
                                  {ep.hn || "-"}
                                </span>
                              </div>

                              {hasNote && (
                                <div
                                  className="monitor-case-note"
                                  title={ep.note}
                                >
                                  Note: {ep.note}
                                </div>
                              )}
                            </td>

                            <td>
                              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                <span
                                  className="badge"
                                  style={{
                                    ...compactBadgeStyle,
                                    backgroundColor:
                                      STATUS_VARIANT[ep.status] || "#6c757d",
                                    color: `${ep.status === "Assigned to Doctor" ? "#212529" : "#fff"}`,
                                  }}
                                >
                                  {ep.status || "-"}
                                </span>
                              </div>
                              <div className="d-flex flex-wrap mt-1" style={{ gap: 5 }}>
                                <span
                                  className={`badge badge-${
                                    DOWNLOAD_VARIANT[downloadStatus] ||
                                    "secondary"
                                  }`}
                                  style={compactBadgeStyle}
                                >
                                  DL: {downloadText}
                                </span>
                                <span
                                  className={`badge badge-${
                                    EXTRACT_VARIANT[extractStatus] ||
                                    "secondary"
                                  }`}
                                  style={compactBadgeStyle}
                                >
                                  EX: {extractText}
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex flex-column" style={{ gap: 3 }}>
                                <div
                                  className="d-flex justify-content-between"
                                  style={{ gap: 8 }}
                                >
                                  <span className="fw-semibold text-truncate">
                                    Tech: {assignedTech}
                                  </span>
                                  <span className="small text-muted">
                                    W {getTechCount(assignedTech)}
                                  </span>
                                </div>
                                <div
                                  className="d-flex justify-content-between"
                                  style={{ gap: 8 }}
                                >
                                  <span className="fw-semibold text-truncate">
                                    Doctor: {assignedDoctor}
                                  </span>
                                  <span className="small text-muted">
                                    W {getDoctorCount(assignedDoctor)}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="d-flex flex-wrap" style={{ gap: 5 }}>
                                <span
                                  className={`badge badge-${
                                    TRANSFER_VARIANT[techTransfer] ||
                                    "secondary"
                                  }`}
                                  style={compactBadgeStyle}
                                >
                                  Tech: {mapTransferText(techTransfer)}
                                </span>
                                <span
                                  className={`badge badge-${
                                    TRANSFER_VARIANT[doctorTransfer] ||
                                    "secondary"
                                  }`}
                                  style={compactBadgeStyle}
                                >
                                  Doctor: {mapTransferText(doctorTransfer)}
                                </span>
                              </div>
                              <div className="small text-muted mt-1">
                                Report {fileCounts.reportCount} | Sleep{" "}
                                {fileCounts.sleepStudyCount}
                              </div>
                            </td>

                            <td>
                              <div className="monitor-actions">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  disabled={fileCounts.total === 0}
                                  onClick={() => openAllFilesModal(item)}
                                >
                                  Files {fileCounts.total}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => openEditModal(item)}
                                >
                                  Edit
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {!loading && filteredCases.length > PER_PAGE && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                  <div className="small text-muted mb-2 mb-md-0">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Item
                      disabled={currentPage === 1}
                      onClick={() => currentPage !== 1 && setCurrentPage(1)}
                      style={{
                        minWidth: 44,
                        textAlign: "center",
                        userSelect: "none",
                        color: currentPage === 1 ? "#adb5bd" : "#6c757d",
                        backgroundColor: currentPage === 1 ? "#f8f9fa" : "#fff",
                        borderColor: "#dee2e6",
                      }}
                    >
                      {"<<"}
                    </Pagination.Item>

                    <Pagination.Item
                      disabled={currentPage === 1}
                      onClick={() =>
                        currentPage !== 1 && setCurrentPage(currentPage - 1)
                      }
                      style={{
                        minWidth: 44,
                        textAlign: "center",
                        userSelect: "none",
                        color: currentPage === 1 ? "#adb5bd" : "#6c757d",
                        backgroundColor: currentPage === 1 ? "#f8f9fa" : "#fff",
                        borderColor: "#dee2e6",
                      }}
                    >
                      {"<"}
                    </Pagination.Item>

                    {paginationItems}

                    <Pagination.Item
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        currentPage !== totalPages &&
                        setCurrentPage(currentPage + 1)
                      }
                      style={{
                        minWidth: 44,
                        textAlign: "center",
                        userSelect: "none",
                        color:
                          currentPage === totalPages ? "#adb5bd" : "#6c757d",
                        backgroundColor:
                          currentPage === totalPages ? "#f8f9fa" : "#fff",
                        borderColor: "#dee2e6",
                      }}
                    >
                      {">"}
                    </Pagination.Item>

                    <Pagination.Item
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        currentPage !== totalPages && setCurrentPage(totalPages)
                      }
                      style={{
                        minWidth: 44,
                        textAlign: "center",
                        userSelect: "none",
                        color:
                          currentPage === totalPages ? "#adb5bd" : "#6c757d",
                        backgroundColor:
                          currentPage === totalPages ? "#f8f9fa" : "#fff",
                        borderColor: "#dee2e6",
                      }}
                    >
                      {">>"}
                    </Pagination.Item>
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="g-3 mt-4">
        <Col xl={6} lg={6}>
          <WorkloadTable title="Technician Workload" rows={allTechWorkload} />
        </Col>
        <Col xl={6} lg={6}>
          <WorkloadTable title="Doctor Workload" rows={allDoctorWorkload} />
        </Col>
      </Row>
      <Modal show={emailModal.show} onHide={closeEmailModal} centered size="lg">
        <div className="modal-header">
          <h5 className="modal-title">Send Email</h5>
          <button type="button" className="close" onClick={closeEmailModal}>
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          {(() => {
            const item = emailModal.item || {};
            const ep = item.extendedProps || {};

            return (
              <>
                <div
                  className="mb-4"
                  style={{
                    border: "1px solid #ece8f3",
                    borderRadius: 14,
                    padding: 14,
                    background: "#fcfbfe",
                  }}
                >
                  <div className="fw-semibold" style={{ color: "#2f2a3a" }}>
                    {ep.order || item.title || "-"}
                  </div>
                  <div className="text-muted small">{getPatientName(ep)}</div>
                  <div className="text-muted small">
                    Customer: {ep.customer || "-"}
                  </div>
                  <div className="text-muted small">
                    Last email: {ep.email ? ep.lastestMail || "-" : "Not sent"}
                  </div>
                </div>

                <div className="form-group mb-0">
                  <label className="font-weight-semibold">Email</label>
                  <select
                    className="form-control"
                    value={selectedEmail}
                    disabled={emailModal.emailList.length === 0}
                    onChange={(e) => setSelectedEmail(e.target.value)}
                  >
                    {emailModal.emailList.length === 0 ? (
                      <option value="">No email found</option>
                    ) : (
                      emailModal.emailList.map((email) => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))
                    )}
                  </select>
                  {emailModal.emailList.length > 1 && (
                    <div className="small text-muted mt-2">
                      Send All Email will send to {emailModal.emailList.length}{" "}
                      email(s).
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        <div className="modal-footer justify-content-between">
          <Button
            variant="info"
            disabled={sendingEmail || emailModal.emailList.length === 0}
            onClick={() => handleSendBookingEmail(true)}
          >
            {sendingEmail ? "Sending..." : "Send All Email"}
          </Button>
          <div>
            <Button variant="light" disabled={sendingEmail} onClick={closeEmailModal}>
              Cancel
            </Button>
            <Button
              variant="danger"
              className="ml-3"
              disabled={sendingEmail || !selectedEmail}
              onClick={() => handleSendBookingEmail(false)}
            >
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={fileModal.show} onHide={closeFileModal} centered size="lg">
        <div className="modal-header">
          <h5 className="modal-title">{fileModal.title} Files</h5>
          <button type="button" className="close" onClick={closeFileModal}>
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          {fileModal.files.length === 0 ? (
            <div className="text-center text-muted py-4">No file</div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 70 }}>No.</th>
                    <th>File Name</th>
                    <th style={{ width: 170 }}>Created</th>
                    <th style={{ width: 150 }}>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {fileModal.files.map(({ type, row, fileName }, index) => (
                    <tr key={`${type || fileModal.type}-${row.id}-${fileName}-${index}`}>
                      <td>{index + 1}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-left"
                          onClick={() =>
                            handleOpenUploadedFile(
                              row,
                              type || fileModal.type,
                              fileName,
                            )
                          }
                        >
                          {fileName}
                          {(type || fileModal.type) && (
                            <span className="badge badge-light ml-2">
                              {isSleepStudyCollection(type || fileModal.type)
                                ? (isScoredSleepStudyUpload(row) ? "Scored Sleep Study" : "Unscored Sleep Study")
                                : "Report"}
                            </span>
                          )}
                        </button>
                      </td>
                      <td>{formatDateTime(row.createAt)}</td>
                      <td>{row.createBy || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        show={filePreviewModal.show}
        onHide={closeFilePreviewModal}
        centered
        size="xl"
      >
        <div className="modal-header">
          <h5 className="modal-title text-truncate">{filePreviewModal.title}</h5>
          <button
            type="button"
            className="close"
            onClick={closeFilePreviewModal}
          >
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body p-0">
          {filePreviewModal.url ? (
            <iframe
              title={filePreviewModal.title || "File preview"}
              src={filePreviewModal.url}
              style={{
                width: "100%",
                height: "75vh",
                border: 0,
              }}
            />
          ) : (
            <div className="text-center text-muted py-4">No file</div>
          )}
        </div>
      </Modal>
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <div className="modal-header">
          <h5 className="modal-title">Update Booking</h5>
          <button type="button" className="close" onClick={handleCloseModal}>
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          {selectedCase && (
            <>
              {(() => {
                const ep = selectedCase.extendedProps || {};
                const currentStatus = ep.status || "-";
                const orderText = ep.order || selectedCase.title || "-";
                const typeSymbol = getTypeSymbol(ep.type);

                const downloadStatus = getDownloadStatus(ep);
                const extractStatus = getExtractStatus(ep);
                const scoredStatus = getScoredCaseStatus(ep, selectedCase.ref ? selectedCase.ref.path : "");
                const techTransferStatus = getTechTransferStatus(ep);
                const doctorTransferStatus = getDoctorTransferStatus(ep);
                const reportValidationErrors = getReportValidationErrors(ep);
                const saveBlockReason = getMonitorSaveBlockReason({
                  currentExtendedProps: ep,
                  nextStatus: editStatus,
                  nextTechnician: editTechnician,
                  nextDoctor: editDoctor,
                  bookingPath: selectedCase.ref ? selectedCase.ref.path : "",
                });
                const saveBlockLogLines = saveBlockReason
                  ? getManualStatusLockLog({
                      ep,
                      reason: saveBlockReason,
                      nextStatus: editStatus,
                      nextTechnician: editTechnician,
                      nextDoctor: editDoctor,
                    })
                  : [];

                const stopBangScore = Number(ep.stopBangScore);

                const getProcessStyle = (variant) => {
                  switch (variant) {
                    case "success":
                      return {
                        background: "#26b99a",
                        color: "#fff",
                        border: "1px solid #26b99a",
                      };
                    case "warning":
                      return {
                        background: "#f0ad4e",
                        color: "#fff",
                        border: "1px solid #f0ad4e",
                      };
                    case "danger":
                      return {
                        background: "#dc3545",
                        color: "#fff",
                        border: "1px solid #dc3545",
                      };
                    case "info":
                      return {
                        background: "#17a2b8",
                        color: "#fff",
                        border: "1px solid #17a2b8",
                      };
                    case "primary":
                      return {
                        background: "#6f42c1",
                        color: "#fff",
                        border: "1px solid #6f42c1",
                      };
                    case "secondary":
                    default:
                      return {
                        background: "#4b325d",
                        color: "#fff",
                        border: "1px solid #4b325d",
                      };
                  }
                };

                const getStopBangStyle = (score) => {
                  if (!Number.isFinite(score) || score === 0) {
                    return {
                      background: "#efefef",
                      color: "#666",
                      border: "1px solid #ddd",
                      display: "-",
                    };
                  }

                  if (score >= 7) {
                    return {
                      background: "#7f1d1d",
                      color: "#fff",
                      border: "1px solid #7f1d1d",
                      display: String(score),
                    };
                  }

                  if (score >= 5) {
                    return {
                      background: "#fde4e4",
                      color: "#b42318",
                      border: "1px solid #f5b5b5",
                      display: String(score),
                    };
                  }

                  return {
                    background: "#fff0d6",
                    color: "#a26100",
                    border: "1px solid #ffd591",
                    display: String(score),
                  };
                };

                const stopBangStyle = getStopBangStyle(stopBangScore);

                return (
                  <>
                    <div
                      className="mb-4"
                      style={{
                        border: "1px solid #ece8f3",
                        borderRadius: 16,
                        padding: 16,
                        background: "#fcfbfe",
                      }}
                    >
                      <div
                        className="d-flex align-items-start justify-content-between"
                        style={{ gap: 12 }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: "1.05rem",
                              color: "#2f2a3a",
                              lineHeight: 1.4,
                              marginBottom: 6,
                              wordBreak: "break-word",
                            }}
                          >
                            {getPatientName(ep)}
                          </div>

                          <div className="text-muted small">
                            {`${orderText}${typeSymbol ? ` ${typeSymbol}` : ""}`}
                          </div>

                          <div className="text-muted small">
                            Type: {ep.type || "-"}
                          </div>

                          <div className="text-muted small">
                            Insurance: {ep.healthInsurance || "-"}
                          </div>

                          <div className="text-muted small">
                            Note: {ep.note || "-"}
                          </div>

                          <div className="text-muted small">
                            Email:{" "}
                            {ep.email
                              ? `Sent${ep.lastestMail ? ` (${ep.lastestMail})` : ""}`
                              : "Not sent"}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 8,
                            flexShrink: 0,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                              borderRadius: 999,
                              padding: "8px 14px",
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              backgroundColor:
                                STATUS_VARIANT[currentStatus] || "#6c757d",
                              color:
                                currentStatus === "Assigned to Doctor"
                                  ? "#212529"
                                  : "#fff",
                            }}
                          >
                            {currentStatus}
                          </span>

                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              background: stopBangStyle.background,
                              color: stopBangStyle.color,
                              border: stopBangStyle.border,
                            }}
                          >
                            STOP BANG : {stopBangStyle.display}
                          </span>

                          <Button
                            size="sm"
                            variant={ep.email ? "outline-success" : "outline-info"}
                            onClick={() => openEmailModal(selectedCase)}
                          >
                            <i className="flaticon-381-compass-2 pr-1" />
                            Send Email
                          </Button>
                        </div>
                      </div>
                    </div>

                    {hasMultiFileReviewRequired(ep) && (
                      <div className="alert alert-warning mb-4" role="alert">
                        <div className="font-weight-bold">
                          Review Multiple Files
                        </div>
                        <div className="small mt-1">
                          {ep.officeScoreExtractError ||
                            ep.officeExtractError ||
                            "Multiple sleep study files are preserved in separate folders. Review or merge them before changing status."}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: "#5b516b",
                          marginBottom: 10,
                        }}
                      >
                        Processing Status
                      </div>

                      <Row className="g-3">
                        <Col md={3} sm={6}>
                          <div
                            style={{
                              border: "1px solid #ece8f3",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              minHeight: 88,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "#7a7288",
                                marginBottom: 8,
                                fontWeight: 500,
                              }}
                            >
                              Raw Download
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 32,
                                padding: "6px 12px",
                                borderRadius: 999,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                ...getProcessStyle(
                                  DOWNLOAD_VARIANT[downloadStatus] ||
                                    "secondary",
                                ),
                              }}
                            >
                              {mapDownloadText(downloadStatus)}
                            </span>
                          </div>
                        </Col>

                        <Col md={3} sm={6}>
                          <div
                            style={{
                              border: "1px solid #ece8f3",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              minHeight: 88,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "#7a7288",
                                marginBottom: 8,
                                fontWeight: 500,
                              }}
                            >
                              Scored Files
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 32,
                                padding: "6px 12px",
                                borderRadius: 999,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                ...getProcessStyle(
                                  EXTRACT_VARIANT[scoredStatus] || DOWNLOAD_VARIANT[scoredStatus] ||
                                    "secondary",
                                ),
                              }}
                            >
                              {mapScoredCaseText(scoredStatus)}
                            </span>
                          </div>
                        </Col>

                        <Col md={3} sm={6}>
                          <div
                            style={{
                              border: "1px solid #ece8f3",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              minHeight: 88,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "#7a7288",
                                marginBottom: 8,
                                fontWeight: 500,
                              }}
                            >
                              Raw Extract
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 32,
                                padding: "6px 12px",
                                borderRadius: 999,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                ...getProcessStyle(
                                  EXTRACT_VARIANT[extractStatus] || "secondary",
                                ),
                              }}
                            >
                              {mapExtractText(extractStatus)}
                            </span>
                          </div>
                        </Col>

                        <Col md={3} sm={6}>
                          <div
                            style={{
                              border: "1px solid #ece8f3",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              minHeight: 88,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "#7a7288",
                                marginBottom: 8,
                                fontWeight: 500,
                              }}
                            >
                              Tech Transfer
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 32,
                                padding: "6px 12px",
                                borderRadius: 999,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                ...getProcessStyle(
                                  TRANSFER_VARIANT[techTransferStatus] ||
                                    "secondary",
                                ),
                              }}
                            >
                              {mapTransferText(techTransferStatus)}
                            </span>
                          </div>
                        </Col>

                        <Col md={3} sm={6}>
                          <div
                            style={{
                              border: "1px solid #ece8f3",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              minHeight: 88,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "#7a7288",
                                marginBottom: 8,
                                fontWeight: 500,
                              }}
                            >
                              Doctor Transfer
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 32,
                                padding: "6px 12px",
                                borderRadius: 999,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                lineHeight: 1,
                                ...getProcessStyle(
                                  TRANSFER_VARIANT[doctorTransferStatus] ||
                                    "secondary",
                                ),
                              }}
                            >
                              {mapTransferText(doctorTransferStatus)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {scoredStatus !== "idle" && (
                      <div className="mb-4">
                        <div className="font-weight-bold mb-2">Scored File Sources</div>
                        <BookingMonitorScoredSources
                          ep={ep}
                          bookingPath={selectedCase.ref ? selectedCase.ref.path : ""}
                        />
                      </div>
                    )}

                    {saveBlockReason && (
                      <div
                        className="alert alert-warning mb-4"
                        style={{
                          borderRadius: 12,
                          borderColor: "#f0ad4e",
                          color: "#5b3b00",
                        }}
                      >
                        <div className="font-weight-bold mb-2">
                          Manual change needs confirmation
                        </div>
                        <div className="mb-2">{saveBlockReason}</div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(120px, 180px) minmax(0, 1fr)",
                            gap: "4px 12px",
                            fontSize: "0.82rem",
                          }}
                        >
                          {saveBlockLogLines.map((line) => (
                            <React.Fragment key={line.label}>
                              <span className="font-weight-bold">
                                {line.label}
                              </span>
                              <span>{line.value}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}

                    {reportValidationErrors.length > 0 && (
                      <div
                        className="alert alert-danger mb-4"
                        style={{
                          borderRadius: 12,
                          borderColor: "#dc3545",
                          color: "#5b1118",
                        }}
                      >
                        <div className="font-weight-bold mb-2">
                          Report Validation Error
                        </div>
                        <div className="d-flex flex-column" style={{ gap: 6 }}>
                          {reportValidationErrors.map((error, index) => (
                            <div key={`${error.code || "report"}-${index}`}>
                              <div className="font-weight-bold">
                                {error.code || "REPORT_VALIDATION_ERROR"}
                              </div>
                              <div>{error.message || "Report validation error"}</div>
                              {(error.expected || error.actual) && (
                                <div className="small mt-1">
                                  Expected: {error.expected || "-"} | Actual:{" "}
                                  {error.actual || "-"}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <div
                        className="d-flex flex-wrap align-items-center justify-content-between"
                        style={{
                          border: "1px solid #ece8f3",
                          borderRadius: 14,
                          padding: 12,
                          background: "#fff",
                          gap: 12,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: "#5b516b",
                            }}
                          >
                            Upload Files
                          </div>
                          <div
                            className="d-flex flex-column"
                            style={{ gap: 10, minWidth: 260, maxWidth: 420 }}
                          >
                            {renderInlineFileList(
                              selectedCase,
                              "report",
                              "Report",
                            )}
                            {renderInlineFileList(
                              selectedCase,
                              "sleepstudy",
                              "Unscored Sleep Study",
                              UNSCORED_SLEEP_STUDY_UPLOAD_TYPE,
                            )}
                            {renderInlineFileList(
                              selectedCase,
                              "sleepstudy",
                              "Scored Sleep Study",
                              SCORED_SLEEP_STUDY_UPLOAD_TYPE,
                            )}
                          </div>
                        </div>

                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!!uploadingType}
                            onClick={() =>
                              handleUploadClick(selectedCase, "report")
                            }
                          >
                            {uploadingType === "report"
                              ? "Uploading..."
                              : "Upload Report"}
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            disabled={!!uploadingType}
                            onClick={() =>
                              handleUploadClick(selectedCase, "sleepstudy")
                            }
                          >
                            {uploadingType === "sleepstudy"
                              ? "Uploading..."
                              : "Upload Unscored Sleep Study"}
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            disabled={!!uploadingType}
                            onClick={() => handleUploadClick(selectedCase, "sleepstudy", SCORED_SLEEP_STUDY_UPLOAD_TYPE)}
                          >
                            {uploadingType === "sleepstudy" ? "Uploading..." : "Upload Scored Sleep Study"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Row className="mb-3">
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label>Tech Record</label>
                          <div className="form-control bg-light text-truncate">
                            {getTechRecord(ep)}
                          </div>
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label>Event Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={editStartDate}
                            onChange={(e) => setEditStartDate(e.target.value)}
                          />
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="form-group mb-3">
                          <label>Status</label>
                          <select
                            className="form-control"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                          >
                            {EDIT_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={4}>
                        <div className="form-group mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <label className="mb-0">
                              Latest Sleep Study Date (วันที่ตรวจการนอน)
                            </label>
                            <Button
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                if (isEditingLatestSleepStudyDate) {
                                  setEditLatestSleepStudyDate(
                                    toInputDate(
                                      ep.latestSleepStudyDate ||
                                        selectedCase.start,
                                    ),
                                  );
                                }
                                setIsEditingLatestSleepStudyDate(
                                  !isEditingLatestSleepStudyDate,
                                );
                              }}
                            >
                              {isEditingLatestSleepStudyDate
                                ? "Cancel Edit"
                                : "Edit"}
                            </Button>
                          </div>
                          <input
                            type="date"
                            className="form-control"
                            value={editLatestSleepStudyDate}
                            disabled={!isEditingLatestSleepStudyDate}
                            onChange={(e) =>
                              setEditLatestSleepStudyDate(e.target.value)
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <div className="form-group mb-2">
                          <label>Tech search</label>
                          <input
                            className="form-control"
                            value={techQuery}
                            onChange={(e) => setTechQuery(e.target.value)}
                            placeholder="Search technician"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <label>Tech Score</label>
                          <select
                            className="form-control"
                            value={editTechnician}
                            onChange={(e) => setEditTechnician(e.target.value)}
                            style={{ minHeight: 44 }}
                          >
                            {techPickerRows.map((row) => (
                              <option key={row.name} value={row.name}>
                                {`${formatAssigneeLabel(row)} - ${row.count}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="form-group mb-2">
                          <label>Doctor search</label>
                          <input
                            className="form-control"
                            value={doctorQuery}
                            onChange={(e) => setDoctorQuery(e.target.value)}
                            placeholder="Search doctor"
                            disabled={!doctorEditEnabled}
                          />
                        </div>

                        <div className="form-group mb-2">
                          <label>Doctor Score</label>
                          <select
                            className="form-control"
                            value={editDoctor}
                            onChange={(e) => setEditDoctor(e.target.value)}
                            disabled={!doctorEditEnabled}
                            style={{ minHeight: 44 }}
                          >
                            {doctorPickerRows.map((row) => (
                              <option key={row.name} value={row.name}>
                                {`${formatAssigneeLabel(row)} - ${row.count}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <div className="form-group mb-3">
                          <label>Note</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            placeholder="Enter note"
                          />
                        </div>
                      </Col>
                    </Row>
                  </>
                );
              })()}
            </>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="light" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleSave()}
            disabled={saving || !selectedCase}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </Modal>
      <Modal
        show={manualOverrideModal.show}
        onHide={closeManualOverrideModal}
        centered
        backdrop="static"
        style={{ zIndex: 1080 }}
      >
        <div className="modal-header">
          <h5 className="modal-title">Confirm Manual Status Change</h5>
          <button
            type="button"
            className="close"
            onClick={closeManualOverrideModal}
            disabled={saving}
          >
            <span>&times;</span>
          </button>
        </div>

        <div className="modal-body">
          <div
            className="alert alert-warning mb-3"
            style={{
              borderRadius: 12,
              borderColor: "#f0ad4e",
              color: "#5b3b00",
            }}
          >
            <div className="font-weight-bold mb-2">Workflow lock found</div>
            <div>{manualOverrideModal.reason}</div>
            {manualOverrideModal.reason.includes("review/merge") && (
              <div className="font-weight-bold mt-3">
                Continue only after you have reviewed or merged the separate
                case folders. This confirmation is saved in the audit log.
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid #ece8f3",
              borderRadius: 12,
              padding: 12,
              background: "#fff",
            }}
          >
            {editStatus === ASSIGNED_TO_DOCTOR_STATUS &&
              isScoredCaseReviewRequired((selectedCase && selectedCase.extendedProps) || {}) && (
                <div className="mb-3">
                  <BookingMonitorScoredSources
                    ep={(selectedCase && selectedCase.extendedProps) || {}}
                    bookingPath={selectedCase && selectedCase.ref ? selectedCase.ref.path : ""}
                    selectedPath={reviewedScoredSourcePath}
                    onSelect={setReviewedScoredSourcePath}
                    disabled={saving}
                  />
                </div>
              )}
            <div className="font-weight-bold mb-2">Lock Log</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(130px, 190px) minmax(0, 1fr)",
                gap: "6px 12px",
                fontSize: "0.86rem",
              }}
            >
              {manualOverrideModal.log.map((line) => (
                <React.Fragment key={line.label}>
                  <span className="text-muted">{line.label}</span>
                  <span style={{ wordBreak: "break-word" }}>{line.value}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button
            variant="light"
            onClick={closeManualOverrideModal}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={confirmManualOverride}
            disabled={saving || (editStatus === ASSIGNED_TO_DOCTOR_STATUS && (
              editDoctor === "กรุณาเลือก" ||
              getPendingScoredUploadPaths((selectedCase && selectedCase.extendedProps) || {},
                selectedCase && selectedCase.ref ? selectedCase.ref.path : "").length > 0 ||
              (isScoredCaseReviewRequired((selectedCase && selectedCase.extendedProps) || {})
                ? !reviewedScoredSourcePath
                : !isScoredCaseReadyForDoctor((selectedCase && selectedCase.extendedProps) || {},
                    selectedCase && selectedCase.ref ? selectedCase.ref.path : ""))
            ))}
          >
            {saving
              ? "Saving..."
              : manualOverrideModal.reason.includes("review/merge")
                ? "Confirm Review & Change Status"
                : "Approve Manual Change"}
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
}

export default SleepStudyMonitor;
